import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import {
  MapPin,
  Plus,
  Radio,
  RefreshCw,
  Trash2,
  Upload,
  Users,
  ImageOff,
  Crop,
  Camera,
  FileDown,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Grid3x3,
} from 'lucide-react'
import { Button, Input, Badge, Select } from '../ui'
import { useAuth, useTenantScope } from '../../context/FloorPlanScopeContext'
import { useReloadOnScopeChange } from '../../hooks/useReloadOnScopeChange'
import {
  createNetworkingFloor,
  deleteNetworkingFloor,
  fetchAlerts,
  fetchDevices,
  fetchNetworkingFloorplan,
  fetchNetworkingFloors,
  placeNetworkingFloorAp,
  removeNetworkingFloorAp,
  saveNetworkingFloorGeometry,
  updateNetworkingFloorAp,
  updateNetworkingFloor,
  runClientSpeedTest,
} from '../../api/floorPlanApi'
import { mapWirelessInventory, withDemoInventoryFallback } from '../../config/demoDevices'
import {
  buildHeatmap,
  drawAlarms,
  drawAp,
  drawClients,
  drawFloorBase,
  drawHeatmapLayer,
  drawInfraDevice,
  drawObstacles,
  drawRoomPreview,
  drawRooms,
  drawSuggestedAp,
  drawSnapGrid,
  drawWalls,
  FLOOR_PAD,
} from '../../lib/floorPlan/canvasRenderer'
import {
  OBSTACLE_TYPES,
  DEFAULT_SNAP_GRID_PCT,
  SNAP_GRID_OPTIONS,
  WALL_MATERIALS,
} from '../../lib/floorPlan/materials'
import {
  createObstacle,
  metersToFootprintPct,
  obstaclePreset,
  resolveObstacleFootprint,
  resyncObstacleFootprintsForFloor,
} from '../../lib/floorPlan/obstacles'
import { createWallSegment, DEFAULT_WALL_HEIGHT_M, DEFAULT_WALL_THICKNESS_M, type WallDims } from '../../lib/floorPlan/walls'
import { formatRfMetric, rfMetricsAtPoint, computeHeatmapGrid } from '../../lib/floorPlan/rfSimulation'
import { computeRfRecommendations } from '../../lib/floorPlan/rfRecommendations'
import { optimizeFloorPlan, applyOptimizationMetrics, computeFloorMetrics, type FloorPlanOptimizationResult } from '../../lib/floorPlan/rfOptimizer'
import { reconcileApAfterMove } from '../../lib/floorPlan/rfOptimizerGeometry'
import { computeRequirementPassSummary, apsForRfAnalysis } from '../../lib/floorPlan/rfFloorMetrics'
import { evaluateInterferencePlan } from '../../lib/floorPlan/rfInterferencePolicy'
import { RF_GRID_COLS, RF_GRID_ROWS } from '../../lib/floorPlan/rfGrid'
import { apDefaultsFromInventory } from '../../lib/floorPlan/apModels'
import { downloadFloorReport } from '../../lib/floorPlan/exportFloorReport'
import FloorPlanOptimizeConfirm from './FloorPlanOptimizeConfirm'
import FloorPlanNoApDialog from './FloorPlanNoApDialog'
import type { FloorPlanOptimizationProgress } from '../../lib/floorPlan/rfOptimizerProgress'
import { paceSavingPhase } from '../../lib/floorPlan/rfOptimizerProgress'
import FloorPlanOptimizationProgressPanel from './FloorPlanOptimizationProgress'
import FloorPlanRfLiveMetrics from './FloorPlanRfLiveMetrics'
import { applyRecommendationFixInMemory, buildGapApPlacement, gapApPlaceRequestBody } from '../../lib/floorPlan/rfRecommendationApply'
import {
  applyChannelPlan,
  createPerimeterWalls,
  findGapApPosition,
  MIN_GAP_AP_SEPARATION_M,
  noAvailableApDialogContent,
  pickInventoryApName,
  type NoAvailableApDialogContent,
} from '../../lib/floorPlan/rfRecommendationFixes'
import {
  defaultGeometry,
  parseGeometry,
  type ClientSpeedTestResult,
  type EditorTool,
  type FloorAlarmDot,
  type FloorClientDot,
  type FloorDevice,
  type FloorBackgroundCrop,
  type FloorGeometry,
  type FloorPlanMetadata,
  type FloorObstacle,
  type FloorRecommendation,
  type FloorRoom,
  type FloorWall,
  type PlacedAp,
  type WallMaterial,
} from '../../lib/floorPlan/types'
import { resolveFloorBackgroundUrl } from '../../lib/floorPlan/background'
import { BUILDING_A_GEOMETRY, isBuildingADemoFloor, isBuildingALayoutStale } from '../../lib/floorPlan/buildingADemoLayout'
import { getDemoFloorConfig, isDemoFloorStale } from '../../lib/floorPlan/demoFloors'
import {
  AP_EDGE_MARGIN,
  clampApPosition,
  clampObstacleCenter,
  clampWallSegmentTranslate,
} from '../../lib/floorPlan/floorBounds'
import {
  effectiveBlueprintCrop,
  isAutoBlueprintCrop,
  NO_BLUEPRINT_CROP,
  parseDimensionsFromText,
  prepareFloorPlanImageUpload,
  type BlueprintAnalysis,
} from '../../lib/floorPlan/blueprintImage'
import { clientToPct, computeFloorDrawLayout, hitTestLayoutFromCanvas } from '../../lib/floorPlan/floorLayout'
import {
  applyFloorViewport,
  clientToCanvasPoint,
  DEFAULT_FLOOR_VIEWPORT,
  fitFloorViewportToLayout,
  floorViewportAtBaseline,
  resetFloorViewportToNative,
  viewportZoomPercent,
  type FloorViewport,
  zoomViewportAtCanvasPoint,
} from '../../lib/floorPlan/floorViewport'
import { defaultMountHeight, normalizeMountType } from '../../lib/floorPlan/apMounts'
import { hitApDeleteButton, hitApMarker, hitClientMarker, hitObstacle, hitRoom, hitWall } from '../../lib/floorPlan/hitTest'
import { syncFloorClients, mergeApClientCounts } from '../../lib/floorPlan/floorClients'
import FloorPlanPalette, { FLOOR_PALETTE_MIME, parsePaletteDrop, type PalettePayload } from './FloorPlanPalette'
import {
  pushHistory,
  redoHistory,
  resetHistory,
  undoHistory,
  type GeometryHistoryState,
} from '../../lib/floorPlan/geometryHistory'
import FloorPlanSidebar, { type SidebarTab } from './FloorPlanSidebar'
import FloorPlanCropDialog from './FloorPlanCropDialog'
import FloorPlanFloorGallery from './FloorPlanFloorGallery'

type CropPending =
  | { kind: 'upload'; floorId: string }
  | { kind: 'recrop'; floorId: string }
  | { kind: 'create'; name: string; widthM: number; heightM: number; metadata?: FloorPlanMetadata }

type CropSession = {
  imageUrl: string
  initialCrop?: FloorBackgroundCrop
  suggestedCrop: FloorBackgroundCrop
  pending: CropPending
  analysis?: BlueprintAnalysis
}

type GeometryClip =
  | { kind: 'wall'; data: Omit<FloorWall, 'id'> }
  | { kind: 'room'; data: Omit<FloorRoom, 'id'> }
  | { kind: 'obstacle'; data: Omit<FloorObstacle, 'id'> }
  | { kind: 'device'; data: Omit<FloorDevice, 'id'> }

const GEOMETRY_PASTE_OFFSET_PCT = 3

type LiveGeomDrag =
  | { kind: 'obstacle'; id: string; x: number; y: number }
  | { kind: 'device'; id: string; x: number; y: number }
  | {
      kind: 'room'
      id: string
      x: number
      y: number
      w: number
      h: number
      anchorX: number
      anchorY: number
      baseX: number
      baseY: number
    }
  | {
      kind: 'wall'
      id: string
      x1: number
      y1: number
      x2: number
      y2: number
      anchorX: number
      anchorY: number
      baseX1: number
      baseY1: number
      baseX2: number
      baseY2: number
    }

function applyLiveGeomDrag<T extends { id: string }>(
  items: T[],
  drag: LiveGeomDrag | null,
  apply: (item: T, drag: LiveGeomDrag) => T,
): T[] {
  if (!drag) return items
  return items.map(item => (item.id === drag.id ? apply(item, drag) : item))
}

const PAD = FLOOR_PAD

type FloorMeta = {
  id: string
  name: string
  widthM: number
  heightM: number
  hasBackground?: boolean
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n))
}

function snapPct(v: number, gridPct: number, lo = 3, hi = 97) {
  if (gridPct <= 0) return clamp(v, lo, hi)
  const snapped = Math.round(v / gridPct) * gridPct
  return clamp(Math.round(snapped * 1000) / 1000, lo, hi)
}

function geometryPctBounds() {
  return { lo: 0, hi: 100 }
}

function apPctBounds() {
  return { lo: AP_EDGE_MARGIN, hi: 100 - AP_EDGE_MARGIN }
}

function uid(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  if (tag === 'TEXTAREA') return true
  if (tag === 'SELECT') return true
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type
    return type === 'text' || type === 'password' || type === 'email' || type === 'number' || type === 'search'
  }
  return el.isContentEditable
}

export default function FloorPlanMap() {
  const { orgId, token } = useAuth()
  const { locationFilter, scopeLabel } = useTenantScope()
  const siteRequired = locationFilter === 'ALL'

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const bgImageRef = useRef<HTMLImageElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const createFloorImageRef = useRef<HTMLInputElement>(null)
  const txSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const apNudgeSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const geomSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const paintRef = useRef<() => void>(() => {})
  const scheduleCanvasPaint = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => paintRef.current())
    })
  }, [])
  const drawPreviewRef = useRef<{ start: { x: number; y: number } | null; current: { x: number; y: number } | null }>({
    start: null,
    current: null,
  })
  const liveDragApRef = useRef<{ id: string; x: number; y: number } | null>(null)
  const liveDragGeomRef = useRef<LiveGeomDrag | null>(null)
  const panDragRef = useRef<{ startX: number; startY: number; originPanX: number; originPanY: number; moved: boolean } | null>(
    null,
  )

  const [viewport, setViewport] = useState<FloorViewport>(DEFAULT_FLOOR_VIEWPORT)
  const [zoomBaseline, setZoomBaseline] = useState<FloorViewport>(DEFAULT_FLOOR_VIEWPORT)
  const viewportRef = useRef(viewport)
  viewportRef.current = viewport
  const [isPanning, setIsPanning] = useState(false)

  const updateViewport = useCallback(
    (next: FloorViewport) => {
      viewportRef.current = next
      setViewport(next)
      scheduleCanvasPaint()
    },
    [scheduleCanvasPaint],
  )

  const [floors, setFloors] = useState<FloorMeta[]>([])
  const [floorThumbnails, setFloorThumbnails] = useState<Record<string, string | null>>({})
  const [loadingFloorThumbnails, setLoadingFloorThumbnails] = useState(false)
  const [floorId, setFloorId] = useState('')
  const [siteName, setSiteName] = useState('Site')
  const [floorName, setFloorName] = useState('Floor')
  const [widthM, setWidthM] = useState(100)
  const [heightM, setHeightM] = useState(80)
  const [aps, setAps] = useState<PlacedAp[]>([])
  const [geometry, setGeometry] = useState<FloorGeometry>(defaultGeometry())
  const [alarms, setAlarms] = useState<FloorAlarmDot[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null)
  const [snapToGrid, setSnapToGrid] = useState(true)
  const [snapGridPct, setSnapGridPct] = useState(DEFAULT_SNAP_GRID_PCT)
  const [uploadingBg, setUploadingBg] = useState(false)
  const [tool, setTool] = useState<EditorTool>('select')
  const [wallMaterial, setWallMaterial] = useState<WallMaterial>('drywall')
  const [wallDims, setWallDims] = useState<WallDims>({
    heightM: DEFAULT_WALL_HEIGHT_M,
    thicknessM: DEFAULT_WALL_THICKNESS_M,
  })
  const [deviceType, setDeviceType] = useState<FloorDevice['type']>('switch')
  const [obstacleType, setObstacleType] = useState('server_rack')
  const [obstacleDims, setObstacleDims] = useState({ widthM: 0.6, depthM: 1.0, heightM: 2.0 })
  const [selectedApId, setSelectedApId] = useState<string | null>(null)
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)
  const [clientSpeedTests, setClientSpeedTests] = useState<Record<string, ClientSpeedTestResult>>({})
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
  const [dragApId, setDragApId] = useState<string | null>(null)
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null)
  const [drawCurrent, setDrawCurrent] = useState<{ x: number; y: number } | null>(null)
  const [hover, setHover] = useState<{ x: number; y: number; label: string; ap?: string } | null>(null)
  const [inventoryAps, setInventoryAps] = useState<{ id: string; name: string; model?: string }[]>([])
  const [addApName, setAddApName] = useState('')
  const [showAddFloor, setShowAddFloor] = useState(false)
  const [newFloorName, setNewFloorName] = useState('')
  const [newFloorBuilding, setNewFloorBuilding] = useState('')
  const [newFloorLevel, setNewFloorLevel] = useState('')
  const [newFloorW, setNewFloorW] = useState('36')
  const [newFloorH, setNewFloorH] = useState('30')
  const [newFloorImage, setNewFloorImage] = useState<File | null>(null)
  const [newFloorImagePreview, setNewFloorImagePreview] = useState<string | null>(null)
  const [cropSession, setCropSession] = useState<CropSession | null>(null)
  const [geometryClipHint, setGeometryClipHint] = useState<string | null>(null)
  const [hasGeometryClipboard, setHasGeometryClipboard] = useState(false)
  const [floorActionLoading, setFloorActionLoading] = useState(false)
  const [savingFloorSize, setSavingFloorSize] = useState(false)
  const [selectedWallId, setSelectedWallId] = useState<string | null>(null)
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null)
  const [selectedObstacleId, setSelectedObstacleId] = useState<string | null>(null)
  const [canvasDragOver, setCanvasDragOver] = useState(false)
  const [selectedAlarm, setSelectedAlarm] = useState<FloorAlarmDot | null>(null)
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('view')
  const [applyingRecommendationId, setApplyingRecommendationId] = useState<string | null>(null)
  const [optimizing, setOptimizing] = useState(false)
  const [optimizationOverlayOpen, setOptimizationOverlayOpen] = useState(false)
  const [optimizationComplete, setOptimizationComplete] = useState(false)
  const [optimizationProgress, setOptimizationProgress] = useState<FloorPlanOptimizationProgress | null>(null)
  const [optimizationSaving, setOptimizationSaving] = useState(false)
  const [optimizationResult, setOptimizationResult] = useState<FloorPlanOptimizationResult | null>(null)
  const [pendingOptimization, setPendingOptimization] = useState<FloorPlanOptimizationResult | null>(null)
  const [noApDialog, setNoApDialog] = useState<NoAvailableApDialogContent | null>(null)
  const [applyingOptimization, setApplyingOptimization] = useState(false)
  const [history, setHistory] = useState<GeometryHistoryState>(resetHistory())
  const geometryRef = useRef(geometry)
  geometryRef.current = geometry
  const geometryClipboardRef = useRef<GeometryClip | null>(null)
  const geometryPasteCountRef = useRef(0)
  const clipHintTimerRef = useRef<number | null>(null)
  /** Bumped on each loadFloor start and when APs change locally — drops stale fetch responses. */
  const floorLoadSeqRef = useRef(0)

  const invalidatePendingFloorLoad = useCallback(() => {
    floorLoadSeqRef.current += 1
  }, [])

  const resetCanvasView = useCallback(() => {
    const wrap = wrapRef.current
    if (!wrap || !floorId) return
    const rect = wrap.getBoundingClientRect()
    if (rect.width < 20 || rect.height < 20) return
    const layout = computeFloorDrawLayout(rect.width, rect.height, widthM, heightM, PAD)
    const next = resetFloorViewportToNative(rect.width, rect.height, layout)
    setZoomBaseline(next)
    updateViewport(next)
  }, [floorId, widthM, heightM, updateViewport])

  const fitCanvasView = useCallback(() => {
    const wrap = wrapRef.current
    if (!wrap || !floorId) return
    const rect = wrap.getBoundingClientRect()
    if (rect.width < 20 || rect.height < 20) return
    const layout = computeFloorDrawLayout(rect.width, rect.height, widthM, heightM, PAD)
    const next = fitFloorViewportToLayout(rect.width, rect.height, layout, 1)
    setZoomBaseline(next)
    updateViewport(next)
  }, [floorId, widthM, heightM, updateViewport])

  const resetCanvasViewRef = useRef(resetCanvasView)
  resetCanvasViewRef.current = resetCanvasView

  const dark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  const clientSync = useMemo(
    () => syncFloorClients(aps, geometry, widthM, heightM),
    [aps, geometry, widthM, heightM],
  )
  const clients = useMemo(
    () =>
      clientSync.clients.map(c => ({
        ...c,
        lastSpeedTest: clientSpeedTests[c.id] ?? c.lastSpeedTest ?? null,
      })),
    [clientSync.clients, clientSpeedTests],
  )
  const apsWithCounts = useMemo(
    () => mergeApClientCounts(aps, clients),
    [aps, clients],
  )
  const capacity = clientSync.capacity
  const selectedAp = useMemo(() => apsWithCounts.find(a => a.id === selectedApId) ?? null, [apsWithCounts, selectedApId])
  const selectedClient = useMemo(
    () => clients.find(c => c.id === selectedClientId) ?? null,
    [clients, selectedClientId],
  )
  const connectedClientsForAp = useMemo(
    () => (selectedAp ? clients.filter(c => c.ap === selectedAp.ap) : []),
    [clients, selectedAp],
  )

  const selectClientById = useCallback(
    (clientId: string) => {
      const client = clients.find(c => c.id === clientId)
      if (!client) return
      setSelectedClientId(clientId)
      const ap = apsWithCounts.find(a => a.ap === client.ap)
      if (ap) setSelectedApId(ap.id)
      setSelectedAlarm(null)
      setSelectedWallId(null)
      setSelectedRoomId(null)
      setSelectedObstacleId(null)
      setSelectedDeviceId(null)
      setSidebarTab('inspect')
    },
    [apsWithCounts, clients],
  )

  const handleClientSpeedTestComplete = useCallback((clientId: string, result: ClientSpeedTestResult) => {
    setClientSpeedTests(prev => ({ ...prev, [clientId]: result }))
  }, [])

  const handleRunClientSpeedTest = useCallback(
    async (clientId: string, rssi: number) => {
      if (!floorId) {
        const { simulateClientSpeedTest } = await import('../../lib/floorPlan/clientTelemetry')
        return simulateClientSpeedTest(rssi)
      }
      const json = await runClientSpeedTest(floorId, clientId, orgId, token, rssi)
      return {
        at: String(json.at ?? new Date().toISOString()),
        downloadMbps: Number(json.downloadMbps ?? 0),
        uploadMbps: Number(json.uploadMbps ?? 0),
        latencyMs: Number(json.latencyMs ?? 0),
        jitterMs: Number(json.jitterMs ?? 0),
        packetLossPct: Number(json.packetLossPct ?? 0),
      }
    },
    [floorId, orgId, token],
  )

  const selectedDevice = useMemo(
    () => geometry.devices.find(d => d.id === selectedDeviceId) ?? null,
    [geometry.devices, selectedDeviceId],
  )
  const selectedWall = useMemo(
    () => geometry.walls.find(w => w.id === selectedWallId) ?? null,
    [geometry.walls, selectedWallId],
  )
  const selectedRoom = useMemo(
    () => geometry.rooms.find(r => r.id === selectedRoomId) ?? null,
    [geometry.rooms, selectedRoomId],
  )
  const selectedObstacle = useMemo(
    () => geometry.obstacles.find(o => o.id === selectedObstacleId) ?? null,
    [geometry.obstacles, selectedObstacleId],
  )
  const isFloorEmpty = useMemo(
    () =>
      geometry.walls.length === 0 &&
      geometry.rooms.length === 0 &&
      geometry.obstacles.length === 0 &&
      geometry.devices.length === 0 &&
      aps.length === 0,
    [geometry, aps.length],
  )
  const layers = geometry.layers
  const heatmapGrid = useMemo(
    () => buildHeatmap(apsForRfAnalysis(apsWithCounts, geometry, widthM, heightM), geometry, widthM, heightM),
    [apsWithCounts, geometry, widthM, heightM],
  )
  const recommendations = useMemo(
    () => computeRfRecommendations(apsWithCounts, geometry, widthM, heightM, heatmapGrid),
    [apsWithCounts, geometry, widthM, heightM, heatmapGrid],
  )
  const rfMetrics = useMemo(
    () =>
      apsWithCounts.length > 0
        ? computeFloorMetrics(apsWithCounts, geometry, widthM, heightM, heatmapGrid, !!backgroundImage)
        : null,
    [apsWithCounts, geometry, widthM, heightM, heatmapGrid, backgroundImage],
  )
  const requirementPass = useMemo(
    () =>
      apsWithCounts.length > 0
        ? computeRequirementPassSummary(apsWithCounts, geometry, widthM, heightM, heatmapGrid, !!backgroundImage)
        : null,
    [apsWithCounts, geometry, widthM, heightM, heatmapGrid, backgroundImage],
  )
  const interferencePlan = useMemo(
    () =>
      apsWithCounts.length >= 2
        ? evaluateInterferencePlan(apsForRfAnalysis(apsWithCounts, geometry, widthM, heightM), geometry, widthM, heightM)
        : null,
    [apsWithCounts, geometry, widthM, heightM],
  )
  const suggestedPlacements = useMemo(
    () =>
      recommendations
        .filter(r => r.action === 'add_ap' && r.fix?.newApAt)
        .map(r => {
          const apName = pickInventoryApName(inventoryAps, aps)
          if (!apName || !r.fix?.newApAt) {
            return { id: r.id, x: r.fix!.newApAt!.x, y: r.fix!.newApAt!.y, title: r.title }
          }
          const placement = buildGapApPlacement(
            apName,
            inventoryAps,
            r.fix.newApAt,
            aps,
            geometry,
            widthM,
            heightM,
          )
          return { id: r.id, x: placement.x, y: placement.y, title: r.title }
        }),
    [recommendations, inventoryAps, aps, geometry, widthM, heightM],
  )

  useEffect(() => {
    const p = obstaclePreset(obstacleType)
    setObstacleDims({ widthM: p.widthM, depthM: p.depthM, heightM: p.heightM })
  }, [obstacleType])

  useEffect(() => {
    setViewport(DEFAULT_FLOOR_VIEWPORT)
    setZoomBaseline(DEFAULT_FLOOR_VIEWPORT)
    panDragRef.current = null
    setIsPanning(false)
  }, [floorId])

  const scheduleGeometrySave = useCallback(
    (next: FloorGeometry) => {
      if (!floorId) return
      if (geomSaveTimer.current) clearTimeout(geomSaveTimer.current)
      geomSaveTimer.current = setTimeout(async () => {
        setSaving(true)
        try {
          await saveNetworkingFloorGeometry(floorId, orgId, token, next as unknown as Record<string, unknown>)
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Save geometry failed')
        } finally {
          setSaving(false)
        }
      }, 600)
    },
    [floorId, orgId, token],
  )

  const updateGeometry = useCallback(
    (patch: Partial<FloorGeometry> | ((g: FloorGeometry) => FloorGeometry), recordHistory = true) => {
      setGeometry(prev => {
        const next =
          typeof patch === 'function'
            ? patch(prev)
            : { ...prev, ...patch, layers: { ...prev.layers, ...(patch.layers ?? {}) } }
        if (recordHistory) {
          setHistory(h => pushHistory(h, prev, next))
        }
        scheduleGeometrySave(next)
        return next
      })
    },
    [scheduleGeometrySave],
  )

  const undoGeometry = useCallback(() => {
    setHistory(h => {
      const { state, geometry: prev } = undoHistory(h, geometryRef.current)
      if (prev) {
        setGeometry(prev)
        scheduleGeometrySave(prev)
      }
      return state
    })
  }, [scheduleGeometrySave])

  const redoGeometry = useCallback(() => {
    setHistory(h => {
      const { state, geometry: next } = redoHistory(h, geometryRef.current)
      if (next) {
        setGeometry(next)
        scheduleGeometrySave(next)
      }
      return state
    })
  }, [scheduleGeometrySave])

  const deleteSelectedDevice = useCallback(() => {
    if (!selectedDeviceId) return
    updateGeometry(g => ({ ...g, devices: g.devices.filter(d => d.id !== selectedDeviceId) }))
    setSelectedDeviceId(null)
  }, [selectedDeviceId, updateGeometry])

  const clearGeometrySelection = useCallback(() => {
    setSelectedWallId(null)
    setSelectedRoomId(null)
    setSelectedObstacleId(null)
    setSelectedDeviceId(null)
    setSelectedApId(null)
    setSelectedClientId(null)
    setSelectedAlarm(null)
  }, [])

  const deleteSelectedWall = useCallback(() => {
    if (!selectedWallId) return
    updateGeometry(g => ({ ...g, walls: g.walls.filter(w => w.id !== selectedWallId) }))
    setSelectedWallId(null)
  }, [selectedWallId, updateGeometry])

  const deleteSelectedRoom = useCallback(() => {
    if (!selectedRoomId) return
    updateGeometry(g => ({ ...g, rooms: g.rooms.filter(r => r.id !== selectedRoomId) }))
    setSelectedRoomId(null)
  }, [selectedRoomId, updateGeometry])

  const deleteSelectedObstacle = useCallback(() => {
    if (!selectedObstacleId) return
    updateGeometry(g => ({ ...g, obstacles: g.obstacles.filter(o => o.id !== selectedObstacleId) }))
    setSelectedObstacleId(null)
  }, [selectedObstacleId, updateGeometry])

  const duplicateObstacleFrom = useCallback(
    (source: FloorObstacle, offset = GEOMETRY_PASTE_OFFSET_PCT) => {
      const copy: FloorObstacle = {
        ...source,
        id: uid('obs'),
        x: clamp(source.x + offset, AP_EDGE_MARGIN, 100 - AP_EDGE_MARGIN),
        y: clamp(source.y + offset, AP_EDGE_MARGIN, 100 - AP_EDGE_MARGIN),
      }
      updateGeometry(g => ({ ...g, obstacles: [...g.obstacles, copy] }))
      setSelectedObstacleId(copy.id)
      setSelectedRoomId(null)
      setSelectedWallId(null)
      setSelectedDeviceId(null)
      setSelectedApId(null)
      setSelectedAlarm(null)
      setSidebarTab('inspect')
    },
    [updateGeometry],
  )

  const showGeometryClipHint = useCallback((msg: string) => {
    setGeometryClipHint(msg)
    if (clipHintTimerRef.current) window.clearTimeout(clipHintTimerRef.current)
    clipHintTimerRef.current = window.setTimeout(() => setGeometryClipHint(null), 2500)
  }, [])

  const storeGeometryClipboard = useCallback(
    (clip: GeometryClip) => {
      geometryClipboardRef.current = clip
      geometryPasteCountRef.current = 0
      setHasGeometryClipboard(true)
      showGeometryClipHint(`Copied ${clip.kind} — ⌘V to paste`)
    },
    [showGeometryClipHint],
  )

  const pasteGeometryClip = useCallback(
    (clip: GeometryClip, offset = GEOMETRY_PASTE_OFFSET_PCT) => {
      if (clip.kind === 'wall') {
        const copy: FloorWall = {
          ...clip.data,
          id: uid('wall'),
          x1: clamp(clip.data.x1 + offset, 0, 100),
          y1: clamp(clip.data.y1 + offset, 0, 100),
          x2: clamp(clip.data.x2 + offset, 0, 100),
          y2: clamp(clip.data.y2 + offset, 0, 100),
        }
        updateGeometry(g => ({ ...g, walls: [...g.walls, copy] }))
        setSelectedWallId(copy.id)
        setSelectedObstacleId(null)
        setSelectedRoomId(null)
        setSelectedDeviceId(null)
        showGeometryClipHint('Pasted wall')
      } else if (clip.kind === 'room') {
        const copy: FloorRoom = {
          ...clip.data,
          id: uid('room'),
          name: clip.data.name.endsWith(' copy') ? clip.data.name : `${clip.data.name} copy`,
          x: clamp(clip.data.x + offset, 0, Math.max(0, 100 - clip.data.w)),
          y: clamp(clip.data.y + offset, 0, Math.max(0, 100 - clip.data.h)),
        }
        updateGeometry(g => ({ ...g, rooms: [...g.rooms, copy] }))
        setSelectedRoomId(copy.id)
        setSelectedWallId(null)
        setSelectedObstacleId(null)
        setSelectedDeviceId(null)
        showGeometryClipHint('Pasted room')
      } else if (clip.kind === 'obstacle') {
        duplicateObstacleFrom({ ...clip.data, id: 'clip' }, offset)
        showGeometryClipHint('Pasted obstacle')
      } else if (clip.kind === 'device') {
        const copy: FloorDevice = {
          ...clip.data,
          id: uid('dev'),
          name: `${clip.data.name} copy`,
          x: clamp(clip.data.x + offset, AP_EDGE_MARGIN, 100 - AP_EDGE_MARGIN),
          y: clamp(clip.data.y + offset, AP_EDGE_MARGIN, 100 - AP_EDGE_MARGIN),
        }
        updateGeometry(g => ({ ...g, devices: [...g.devices, copy] }))
        setSelectedDeviceId(copy.id)
        setSelectedWallId(null)
        setSelectedRoomId(null)
        setSelectedObstacleId(null)
        showGeometryClipHint('Pasted device')
      }
      setSelectedApId(null)
      setSelectedAlarm(null)
      setSidebarTab('inspect')
    },
    [duplicateObstacleFrom, showGeometryClipHint, updateGeometry],
  )

  const copySelectedElement = useCallback(() => {
    const g = geometryRef.current
    if (selectedWallId) {
      const w = g.walls.find(x => x.id === selectedWallId)
      if (w) {
        const { id: _id, ...data } = w
        storeGeometryClipboard({ kind: 'wall', data })
        return
      }
    }
    if (selectedRoomId) {
      const r = g.rooms.find(x => x.id === selectedRoomId)
      if (r) {
        const { id: _id, ...data } = r
        storeGeometryClipboard({ kind: 'room', data })
        return
      }
    }
    if (selectedObstacleId) {
      const o = g.obstacles.find(x => x.id === selectedObstacleId)
      if (o) {
        const { id: _id, ...data } = o
        storeGeometryClipboard({ kind: 'obstacle', data })
        return
      }
    }
    if (selectedDeviceId) {
      const d = g.devices.find(x => x.id === selectedDeviceId)
      if (d) {
        const { id: _id, ...data } = d
        storeGeometryClipboard({ kind: 'device', data })
      }
    }
  }, [selectedDeviceId, selectedObstacleId, selectedRoomId, selectedWallId, storeGeometryClipboard])

  const duplicateSelectedElement = useCallback(() => {
    const g = geometryRef.current
    if (selectedWallId) {
      const w = g.walls.find(x => x.id === selectedWallId)
      if (w) {
        const { id: _id, ...data } = w
        storeGeometryClipboard({ kind: 'wall', data })
        pasteGeometryClip({ kind: 'wall', data }, GEOMETRY_PASTE_OFFSET_PCT)
        return
      }
    }
    if (selectedRoomId) {
      const r = g.rooms.find(x => x.id === selectedRoomId)
      if (r) {
        const { id: _id, ...data } = r
        storeGeometryClipboard({ kind: 'room', data })
        pasteGeometryClip({ kind: 'room', data }, GEOMETRY_PASTE_OFFSET_PCT)
        return
      }
    }
    if (selectedObstacleId) {
      const o = g.obstacles.find(x => x.id === selectedObstacleId)
      if (o) {
        const { id: _id, ...data } = o
        storeGeometryClipboard({ kind: 'obstacle', data })
        pasteGeometryClip({ kind: 'obstacle', data }, GEOMETRY_PASTE_OFFSET_PCT)
        return
      }
    }
    if (selectedDeviceId) {
      const d = g.devices.find(x => x.id === selectedDeviceId)
      if (d) {
        const { id: _id, ...data } = d
        storeGeometryClipboard({ kind: 'device', data })
        pasteGeometryClip({ kind: 'device', data }, GEOMETRY_PASTE_OFFSET_PCT)
      }
    }
  }, [
    pasteGeometryClip,
    selectedDeviceId,
    selectedObstacleId,
    selectedRoomId,
    selectedWallId,
    storeGeometryClipboard,
  ])

  const pasteElement = useCallback(() => {
    const clip = geometryClipboardRef.current
    if (!clip) return
    geometryPasteCountRef.current += 1
    pasteGeometryClip(clip, GEOMETRY_PASTE_OFFSET_PCT * geometryPasteCountRef.current)
  }, [pasteGeometryClip])

  const takeFloorScreenshot = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || canvas.width < 1) {
      setError('Nothing to capture — open a floor plan first')
      return
    }
    try {
      const safeName = (floorName || 'floor').replace(/[^\w.-]+/g, '_').slice(0, 48)
      const url = canvas.toDataURL('image/png')
      const a = document.createElement('a')
      a.href = url
      a.download = `${safeName}-${widthM}x${heightM}m.png`
      a.click()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Screenshot failed')
    }
  }, [floorName, heightM, widthM])

  const exportFloorReport = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || canvas.width < 1) {
      setError('Nothing to export — open a floor plan first')
      return
    }
    try {
      downloadFloorReport({
        mapCanvas: canvas,
        floorName: floorName || 'floor',
        widthM,
        heightM,
        bandGHz: geometry.bandGHz,
        metrics: rfMetrics,
        requirementPass,
        interferencePlan,
        aps,
        buildingName: geometry.metadata?.buildingName,
        floorLevel: geometry.metadata?.floorLevel,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Report export failed')
    }
  }, [aps, floorName, geometry.bandGHz, geometry.metadata, heightM, interferencePlan, requirementPass, rfMetrics, widthM])

  const replaceSelectedWallMaterial = useCallback(
    (material: WallMaterial) => {
      if (!selectedWallId) return
      const mat = WALL_MATERIALS.find(m => m.id === material)
      updateGeometry(g => ({
        ...g,
        walls: g.walls.map(w =>
          w.id === selectedWallId
            ? { ...w, material, attenuationDb: mat?.attenuationDb ?? w.attenuationDb }
            : w,
        ),
      }))
    },
    [selectedWallId, updateGeometry],
  )

  const updateSelectedWallDims = useCallback(
    (patch: Partial<WallDims>) => {
      if (!selectedWallId) return
      updateGeometry(g => ({
        ...g,
        walls: g.walls.map(w => {
          if (w.id !== selectedWallId) return w
          return {
            ...w,
            heightM: patch.heightM ?? w.heightM ?? DEFAULT_WALL_HEIGHT_M,
            thicknessM: patch.thicknessM ?? w.thicknessM ?? DEFAULT_WALL_THICKNESS_M,
          }
        }),
      }))
    },
    [selectedWallId, updateGeometry],
  )

  const replaceSelectedObstacleType = useCallback(
    (type: string) => {
      if (!selectedObstacleId) return
      const preset = obstaclePreset(type)
      updateGeometry(g => ({
        ...g,
        obstacles: g.obstacles.map(o => {
          if (o.id !== selectedObstacleId) return o
          const { w, h } = metersToFootprintPct(preset.widthM, preset.depthM, widthM, heightM)
          return {
            ...o,
            type,
            widthM: preset.widthM,
            depthM: preset.depthM,
            heightM: preset.heightM,
            w,
            h,
            attenuationDb: preset.attenuationDb,
          }
        }),
      }))
    },
    [selectedObstacleId, updateGeometry, widthM, heightM],
  )

  const updateSelectedObstacleDims = useCallback(
    (patch: { widthM?: number; depthM?: number; heightM?: number }) => {
      if (!selectedObstacleId) return
      updateGeometry(g => ({
        ...g,
        obstacles: g.obstacles.map(o => {
          if (o.id !== selectedObstacleId) return o
          const resolved = resolveObstacleFootprint(o, widthM, heightM)
          const width = patch.widthM ?? resolved.widthM
          const depth = patch.depthM ?? resolved.depthM
          const height = patch.heightM ?? resolved.heightM
          const { w, h } = metersToFootprintPct(width, depth, widthM, heightM)
          return { ...o, widthM: width, depthM: depth, heightM: height, w, h }
        }),
      }))
    },
    [selectedObstacleId, updateGeometry, widthM, heightM],
  )

  const saveFloorDimensions = useCallback(
    async (wIn: number, hIn: number): Promise<boolean> => {
      if (!floorId) {
        setError('Select a floor before changing size')
        return false
      }
      if (siteRequired) {
        setError('Select a site in the header scope picker before changing floor size')
        return false
      }
      const w = Math.round(wIn)
      const h = Math.round(hIn)
      if (!Number.isFinite(w) || !Number.isFinite(h) || w < 5 || w > 500 || h < 5 || h > 500) {
        setError('Floor size must be between 5 and 500 meters')
        return false
      }
      if (w === widthM && h === heightM) return true

      setSavingFloorSize(true)
      setError(null)
      const locationId = locationFilter === 'ALL' ? null : locationFilter
      try {
        const resp = await updateNetworkingFloor(
          floorId,
          orgId,
          token,
          { widthM: w, heightM: h },
          locationId,
        )
        const savedW = Number(resp.widthM ?? w)
        const savedH = Number(resp.heightM ?? h)
        if (savedW !== w || savedH !== h) {
          setError(`Server saved ${savedW}×${savedH} m instead of ${w}×${h} m — reload the floor if values look wrong`)
        }
        flushSync(() => {
          setWidthM(savedW)
          setHeightM(savedH)
          setFloors(prev => prev.map(f => (f.id === floorId ? { ...f, widthM: savedW, heightM: savedH } : f)))
          updateGeometry(
            g => ({
              ...g,
              obstacles: resyncObstacleFootprintsForFloor(g.obstacles, savedW, savedH),
            }),
            false,
          )
        })
        scheduleCanvasPaint()
        return true
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Update floor size failed')
        return false
      } finally {
        setSavingFloorSize(false)
      }
    },
    [floorId, heightM, locationFilter, orgId, scheduleCanvasPaint, siteRequired, token, updateGeometry, widthM],
  )

  const renameSelectedRoom = useCallback(
    (name: string) => {
      if (!selectedRoomId) return
      updateGeometry(g => ({
        ...g,
        rooms: g.rooms.map(r => (r.id === selectedRoomId ? { ...r, name: name.trim() || r.name } : r)),
      }))
    },
    [selectedRoomId, updateGeometry],
  )

  const updateSelectedRoomCoverage = useCallback(
    (coverageRequirement: { minRssiDbm: number } | undefined) => {
      if (!selectedRoomId) return
      updateGeometry(g => ({
        ...g,
        rooms: g.rooms.map(r =>
          r.id === selectedRoomId ? { ...r, coverageRequirement } : r,
        ),
      }))
    },
    [selectedRoomId, updateGeometry],
  )

  const clearAllMapElements = useCallback(() => {
    if (
      !window.confirm(
        'Remove all walls, rooms, obstacles, and infrastructure devices from this floor? APs are kept.',
      )
    ) {
      return
    }
    updateGeometry(g => ({ ...g, walls: [], rooms: [], obstacles: [], devices: [] }))
    clearGeometrySelection()
  }, [clearGeometrySelection, updateGeometry])

  function placePaletteItem(payload: PalettePayload, pct: { x: number; y: number }) {
    if (payload.kind === 'obstacle') {
      const base = createObstacle(payload.type, pct.x, pct.y, widthM, heightM, obstacleDims)
      const pos = clampObstacleCenter(base.x, base.y, base, widthM, heightM, geometry)
      updateGeometry(g => ({
        ...g,
        obstacles: [...g.obstacles, { ...base, id: uid('obs'), x: pos.x, y: pos.y }],
      }))
      setTool('select')
      setSidebarTab('draw')
      return
    }
    if (payload.kind === 'device') {
      updateGeometry(g => ({
        ...g,
        devices: [
          ...g.devices,
          {
            id: uid('dev'),
            type: payload.type,
            name: `${payload.type}-${g.devices.length + 1}`,
            x: pct.x,
            y: pct.y,
            status: 'healthy',
            heightM: 2.5,
            mount: 'ceiling',
          },
        ],
      }))
      setTool('select')
      return
    }
    if (payload.kind === 'ap' && floorId) {
      const inv = inventoryAps.find(a => a.id === payload.deviceId || a.name === payload.apName)
      const defaults = apDefaultsFromInventory(inv, geometry.bandGHz)
      void placeNetworkingFloorAp(floorId, orgId, token, {
        apName: payload.apName,
        x: pct.x,
        y: pct.y,
        deviceId: payload.deviceId,
        txPowerDbm: defaults.txPowerDbm,
        mount: defaults.mount,
        mountHeightM: defaults.mountHeightM,
        band: defaults.band,
        ...(defaults.mount === 'wall' ? { mountOrientationDeg: 0 } : {}),
      }).then(p => {
        invalidatePendingFloorLoad()
        setAps(prev => [
          ...prev,
          {
            id: String(p.id),
            ap: String(p.ap),
            x: Number(p.x),
            y: Number(p.y),
            clients: Number(p.clients ?? 0),
            rssiZone: String(p.rssiZone ?? 'medium'),
            txPowerDbm: defaults.txPowerDbm,
            deviceId: payload.deviceId,
            mount: defaults.mount,
            mountHeightM: defaults.mountHeightM,
            band: defaults.band,
          },
        ])
      })
      setSidebarTab('aps')
    }
  }

  const paint = useCallback(() => {
    const canvas = canvasRef.current
    const wrap = wrapRef.current
    if (!canvas) return
    const rect = (wrap ?? canvas).getBoundingClientRect()
    const lw = rect.width
    const lh = rect.height
    if (lw < 20) return
    const dpr = window.devicePixelRatio || 1
    canvas.width = Math.floor(lw * dpr)
    canvas.height = Math.floor(lh * dpr)
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.fillStyle = dark ? '#0f172a' : '#e2e8f0'
    ctx.fillRect(0, 0, lw, lh)

    const vp = viewportRef.current
    ctx.save()
    applyFloorViewport(ctx, vp)

    if (layers.layout) {
      drawFloorBase(ctx, lw, lh, widthM, heightM, dark, bgImageRef.current, geometry.backgroundCrop)
      if (snapToGrid && snapGridPct > 0) {
        drawSnapGrid(ctx, lw, lh, widthM, heightM, snapGridPct, dark, !!geometry.backgroundCrop)
      }
    }
    if (layers.rooms) {
      const rooms = applyLiveGeomDrag(geometry.rooms, liveDragGeomRef.current, (r, d) =>
        d.kind === 'room' ? { ...r, x: d.x, y: d.y } : r,
      )
      drawRooms(ctx, lw, lh, widthM, heightM, rooms, dark, selectedRoomId)
    }
    const preview =
      drawPreviewRef.current.start && drawPreviewRef.current.current
        ? {
            id: 'preview',
            x1: drawPreviewRef.current.start.x,
            y1: drawPreviewRef.current.start.y,
            x2: drawPreviewRef.current.current.x,
            y2: drawPreviewRef.current.current.y,
            material: wallMaterial,
            heightM: wallDims.heightM,
            thicknessM: wallDims.thicknessM,
          }
        : tool === 'wall' && drawStart && drawCurrent
          ? {
              id: 'preview',
              x1: drawStart.x,
              y1: drawStart.y,
              x2: drawCurrent.x,
              y2: drawCurrent.y,
              material: wallMaterial,
              heightM: wallDims.heightM,
              thicknessM: wallDims.thicknessM,
            }
          : null
    const walls = applyLiveGeomDrag(geometry.walls, liveDragGeomRef.current, (w, d) =>
      d.kind === 'wall' ? { ...w, x1: d.x1, y1: d.y1, x2: d.x2, y2: d.y2 } : w,
    )
    const obstacles = applyLiveGeomDrag(geometry.obstacles, liveDragGeomRef.current, (o, d) =>
      d.kind === 'obstacle' ? { ...o, x: d.x, y: d.y } : o,
    )
    if (layers.walls) drawWalls(ctx, lw, lh, widthM, heightM, walls, null, selectedWallId)
    if (layers.layout && obstacles.length > 0)
      drawObstacles(ctx, lw, lh, obstacles, selectedObstacleId, widthM, heightM)
    else if (layers.heatmap && obstacles.length > 0)
      drawObstacles(ctx, lw, lh, obstacles, selectedObstacleId, widthM, heightM)
    if (tool === 'wall' && preview) drawWalls(ctx, lw, lh, widthM, heightM, [], preview)
    if (tool === 'room' && drawPreviewRef.current.start && drawPreviewRef.current.current) {
      drawRoomPreview(
        ctx,
        lw,
        lh,
        widthM,
        heightM,
        drawPreviewRef.current.start.x,
        drawPreviewRef.current.start.y,
        drawPreviewRef.current.current.x,
        drawPreviewRef.current.current.y,
      )
    } else if (tool === 'room' && drawStart && drawCurrent) {
      drawRoomPreview(ctx, lw, lh, widthM, heightM, drawStart.x, drawStart.y, drawCurrent.x, drawCurrent.y)
    }
    if (layers.heatmap && aps.length > 0) {
      const live = liveDragApRef.current
      const heatAps = live
        ? aps.map(a => (a.id === live.id ? { ...a, x: live.x, y: live.y } : a))
        : aps
      drawHeatmapLayer(ctx, lw, lh, heatAps, geometry, widthM, heightM, heatmapGrid, RF_GRID_COLS, RF_GRID_ROWS)
    }
    if (
      geometry.showSuggestedPlacements !== false &&
      suggestedPlacements.length > 0 &&
      (layers.heatmap || layers.devices)
    ) {
      for (const s of suggestedPlacements) {
        drawSuggestedAp(ctx, lw, lh, s.x, s.y, widthM, heightM, s.title)
      }
    }
    if (layers.devices) {
      const devices = applyLiveGeomDrag(geometry.devices, liveDragGeomRef.current, (d, drag) =>
        drag.kind === 'device' ? { ...d, x: drag.x, y: drag.y } : d,
      )
      for (const d of devices) {
        const show =
          d.type === 'camera'
            ? layers.cameras
            : d.type === 'sensor'
              ? layers.sensors
              : true
        if (show) drawInfraDevice(ctx, lw, lh, widthM, heightM, d, d.id === selectedDeviceId)
      }
    }
    if (layers.devices) {
      for (const ap of apsWithCounts) {
        const live = liveDragApRef.current
        const drawAp_ =
          live && live.id === ap.id ? { ...ap, x: live.x, y: live.y } : ap
        drawAp(ctx, lw, lh, drawAp_, ap.id === selectedApId || ap.id === dragApId, widthM, heightM, geometry.bandGHz)
      }
    }
    if (layers.clients) {
      drawClients(ctx, lw, lh, widthM, heightM, clients, {
        aps: apsWithCounts,
        selectedApName: selectedAp?.ap ?? null,
        selectedClientId,
      })
    }
    if (layers.alarms) drawAlarms(ctx, lw, lh, widthM, heightM, alarms)

    ctx.restore()
  }, [
    apsWithCounts,
    alarms,
    clients,
    dark,
    dragApId,
    drawCurrent,
    drawStart,
    geometry,
    heatmapGrid,
    suggestedPlacements,
    layers,
    selectedAp,
    selectedApId,
    selectedClientId,
    selectedDeviceId,
    selectedObstacleId,
    selectedRoomId,
    selectedWallId,
    snapGridPct,
    snapToGrid,
    tool,
    viewport,
    wallMaterial,
    widthM,
    heightM,
  ])

  useEffect(() => {
    paintRef.current = paint
  }, [paint])

  useEffect(() => {
    scheduleCanvasPaint()
  }, [paint, scheduleCanvasPaint])

  useEffect(() => {
    scheduleCanvasPaint()
  }, [sidebarTab, floorId, loading, scheduleCanvasPaint])

  useEffect(() => {
    if (!backgroundImage) {
      bgImageRef.current = null
      paint()
      return
    }
    const img = new Image()
    img.onload = () => {
      bgImageRef.current = img
      paint()
    }
    img.onerror = () => {
      bgImageRef.current = null
      setError(`Floor plan image failed to load (${backgroundImage})`)
      paint()
    }
    img.src = backgroundImage
  }, [backgroundImage, paint])

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      resetCanvasViewRef.current()
      scheduleCanvasPaint()
    })
    ro.observe(el)
    resetCanvasViewRef.current()
    return () => ro.disconnect()
  }, [scheduleCanvasPaint, floorId, widthM, heightM])

  useEffect(() => {
    if (!loading && floorId) resetCanvasView()
  }, [loading, floorId, widthM, heightM, resetCanvasView])

  const loadFloors = useCallback(async () => {
    const res = await fetchNetworkingFloors(orgId, token)
    const list = ((res.data as Record<string, unknown>[]) ?? []).map(row => ({
      id: String(row.id ?? ''),
      name: String(row.name ?? 'Floor'),
      widthM: Number(row.widthM) || 100,
      heightM: Number(row.heightM) || 80,
      hasBackground: Boolean(row.hasBackground),
    }))
    setFloors(list)
    setFloorId(prev => {
      if (prev && list.some(f => String(f.id) === prev)) return prev
      return list[0] ? String(list[0].id) : ''
    })
    return list
  }, [orgId, token])

  useEffect(() => {
    if (floors.length === 0) {
      setFloorThumbnails({})
      return
    }

    let cancelled = false
    setLoadingFloorThumbnails(true)

    void (async () => {
      const next: Record<string, string | null> = {}
      await Promise.all(
        floors.map(async floor => {
          if (!floor.hasBackground) {
            next[floor.id] = null
            return
          }
          try {
            const fp = await fetchNetworkingFloorplan(orgId, token, floor.id)
            const bgRaw = String(fp.backgroundImage ?? '')
            next[floor.id] = bgRaw.length > 0 ? resolveFloorBackgroundUrl(bgRaw) : null
          } catch {
            next[floor.id] = null
          }
        }),
      )
      if (!cancelled) {
        setFloorThumbnails(prev => ({ ...prev, ...next }))
        setLoadingFloorThumbnails(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [floors, orgId, token])

  useEffect(() => {
    if (!floorId || !backgroundImage) return
    setFloorThumbnails(prev => ({ ...prev, [floorId]: backgroundImage }))
  }, [backgroundImage, floorId])

  const openCropFromFile = useCallback(
    async (
      file: File,
      pending: CropPending,
      hints?: { widthM?: number; heightM?: number; floorName?: string },
    ) => {
      setUploadingBg(true)
      setError(null)
      try {
        const { url, analysis } = await prepareFloorPlanImageUpload(file, hints)
        setCropSession({
          imageUrl: url,
          initialCrop: pending.kind === 'recrop' ? geometryRef.current.backgroundCrop : undefined,
          suggestedCrop: analysis.crop,
          pending,
          analysis,
        })
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Image prepare failed')
        throw e
      } finally {
        setUploadingBg(false)
      }
    },
    [],
  )

  const applyCropSession = useCallback(
    async (crop: FloorBackgroundCrop) => {
      const session = cropSession
      if (!session) return
      setCropSession(null)
      setFloorActionLoading(true)
      setError(null)
      try {
        const { imageUrl, pending, analysis } = session
        if (pending.kind === 'upload' || pending.kind === 'recrop') {
          const id = pending.floorId
          if (pending.kind === 'upload') {
            await updateNetworkingFloor(id, orgId, token, { backgroundImage: imageUrl })
            setBackgroundImage(imageUrl)
          }
          const nextGeo = { ...geometryRef.current, backgroundCrop: effectiveBlueprintCrop(crop) }
          setGeometry(nextGeo)
          await saveNetworkingFloorGeometry(id, orgId, token, nextGeo as unknown as Record<string, unknown>)
          if (analysis?.aspectWarning) setError(analysis.aspectWarning)
        } else if (pending.kind === 'create') {
          const created = await createNetworkingFloor(orgId, token, {
            name: pending.name,
            widthM: pending.widthM,
            heightM: pending.heightM,
          })
          const id = String(created.id ?? '')
          if (id) {
            const geo = {
              ...defaultGeometry(),
              backgroundCrop: effectiveBlueprintCrop(crop),
              metadata: pending.metadata,
            }
            await updateNetworkingFloor(id, orgId, token, {
              backgroundImage: imageUrl,
              snapGridPct: DEFAULT_SNAP_GRID_PCT,
              widthM: pending.widthM,
              heightM: pending.heightM,
            })
            await saveNetworkingFloorGeometry(id, orgId, token, geo as unknown as Record<string, unknown>)
            setShowAddFloor(false)
            setNewFloorName('')
            setNewFloorImage(null)
            setNewFloorImagePreview(null)
            await loadFloors()
            setFloorId(id)
            setSidebarTab('draw')
            setTool('select')
            if (analysis?.aspectWarning) setError(analysis.aspectWarning)
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Apply crop failed')
      } finally {
        setFloorActionLoading(false)
      }
    },
    [cropSession, loadFloors, orgId, token],
  )

  const openRecropDialog = useCallback(() => {
    if (!floorId || !backgroundImage) return
    setCropSession({
      imageUrl: backgroundImage,
      initialCrop: geometry.backgroundCrop,
      suggestedCrop: geometry.backgroundCrop ?? NO_BLUEPRINT_CROP,
      pending: { kind: 'recrop', floorId },
    })
  }, [backgroundImage, floorId, geometry.backgroundCrop])

  const handleCreateFloor = useCallback(async () => {
    setError(null)
    const name = newFloorName.trim() || 'New floor'
    const w = Number(newFloorW) || parseDimensionsFromText(name)?.widthM || 36
    const h = Number(newFloorH) || parseDimensionsFromText(name)?.heightM || 30
    const metadata: FloorPlanMetadata | undefined =
      newFloorBuilding.trim() || newFloorLevel.trim()
        ? {
            buildingName: newFloorBuilding.trim() || undefined,
            floorLevel: newFloorLevel.trim() || undefined,
          }
        : undefined

    if (newFloorImage) {
      try {
        await openCropFromFile(
          newFloorImage,
          { kind: 'create', name, widthM: w, heightM: h, metadata },
          { floorName: name, widthM: w, heightM: h },
        )
      } catch {
        /* error already set */
      }
      return
    }

    setFloorActionLoading(true)
    try {
      const created = await createNetworkingFloor(orgId, token, { name, widthM: w, heightM: h })
      const id = String(created.id ?? '')
      if (id) {
        await saveNetworkingFloorGeometry(id, orgId, token, {
          ...defaultGeometry(),
          metadata,
        } as unknown as Record<string, unknown>)
      }
      setShowAddFloor(false)
      setNewFloorName('')
      setNewFloorBuilding('')
      setNewFloorLevel('')
      setNewFloorImage(null)
      setNewFloorImagePreview(null)
      await loadFloors()
      if (id) {
        setFloorId(id)
        setSidebarTab('draw')
        setTool('select')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create floor failed')
    } finally {
      setFloorActionLoading(false)
    }
  }, [loadFloors, newFloorBuilding, newFloorH, newFloorImage, newFloorLevel, newFloorName, newFloorW, openCropFromFile, orgId, token])

  const handleDeleteFloor = useCallback(async () => {
    if (!floorId) return
    const target = floors.find(f => String(f.id) === floorId)
    const label = target?.name ?? 'this floor'
    if (!window.confirm(`Delete floor "${label}"? This cannot be undone.`)) return
    setFloorActionLoading(true)
    setError(null)
    try {
      await deleteNetworkingFloor(floorId, orgId, token)
      await loadFloors()
      setAps([])
      setBackgroundImage(null)
      clearGeometrySelection()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete floor failed')
    } finally {
      setFloorActionLoading(false)
    }
  }, [floorId, floors, loadFloors, orgId, token, clearGeometrySelection])

  const loadFloor = useCallback(
    async (id: string) => {
      if (!id) return
      const seq = ++floorLoadSeqRef.current
      setLoading(true)
      setError(null)
      try {
        const fp = await fetchNetworkingFloorplan(orgId, token, id)
        if (seq !== floorLoadSeqRef.current) return
        let geo = parseGeometry(fp.geometry)
        const bgRaw = String(fp.backgroundImage ?? '')

        const demoConfig = getDemoFloorConfig(id, bgRaw)
        let geoMigrated = false
        if (demoConfig && isDemoFloorStale(id, bgRaw, fp, geo, demoConfig)) {
          geo = demoConfig.geometry
          geoMigrated = true
        }
        if (isBuildingADemoFloor(id, bgRaw) && isBuildingALayoutStale(id, fp, geo)) {
          geo = BUILDING_A_GEOMETRY
          geoMigrated = true
        }
        if (bgRaw.length > 0 && geo.backgroundCrop && isAutoBlueprintCrop(geo.backgroundCrop)) {
          geo = { ...geo, backgroundCrop: undefined }
          geoMigrated = true
        }
        if (geoMigrated) {
          await saveNetworkingFloorGeometry(id, orgId, token, geo as unknown as Record<string, unknown>)
          if (seq !== floorLoadSeqRef.current) return
        }

        setSiteName(String(fp.siteName ?? fp.site ?? 'Site'))
        setFloorName(String(fp.floorName ?? fp.site ?? 'Floor'))
        setWidthM(Number(fp.widthM) || 100)
        setHeightM(Number(fp.heightM) || 80)
        const bgResolved = bgRaw.length > 0 ? resolveFloorBackgroundUrl(bgRaw) : null
        setBackgroundImage(bgResolved)
        clearGeometrySelection()
        const grid = Number(fp.snapGridPct ?? DEFAULT_SNAP_GRID_PCT)
        setSnapGridPct(grid > 0 ? grid : DEFAULT_SNAP_GRID_PCT)
        setSnapToGrid(grid !== 0)
        const raw = (fp.accessPoints as Record<string, unknown>[]) ?? []
        const apList = raw.map(r => ({
          id: String(r.id ?? r.ap),
          ap: String(r.ap),
          x: Number(r.x),
          y: Number(r.y),
          clients: Number(r.clients ?? 0),
          rssiZone: String(r.rssiZone ?? 'medium'),
          band: String(r.band ?? 'dual'),
          txPowerDbm: Number(r.txPowerDbm ?? 15),
          channel: geo.apChannels?.[String(r.id ?? r.ap)],
          deviceId: r.deviceId ? String(r.deviceId) : undefined,
          mount: normalizeMountType(r.mount),
          mountHeightM:
            r.mountHeightM != null ? Number(r.mountHeightM) : defaultMountHeight(normalizeMountType(r.mount)),
          mountOrientationDeg:
            r.mountOrientationDeg != null ? Number(r.mountOrientationDeg) : undefined,
        }))
        if (seq !== floorLoadSeqRef.current) return

        setAps(apList)
        setOptimizationResult(null)
        setGeometry({
          ...geo,
          layers: {
            ...geo.layers,
            heatmap: apList.length > 0 ? geo.layers.heatmap : false,
          },
        })
        setHistory(resetHistory())
        const alertRes = await fetchAlerts(orgId, token, { status: 'FIRING' })
        if (seq !== floorLoadSeqRef.current) return
        const alertList = (alertRes.data as { id?: string; device?: string; severity?: string; title?: string }[]) ?? []
        const alarmDots: FloorAlarmDot[] = []
        for (const a of alertList.slice(0, 12)) {
          const ap = apList.find(r => String(r.ap) === a.device || String(r.ap).includes(String(a.device ?? '')))
          const dev = geo.devices.find(d => d.name === a.device)
          const x = ap ? Number(ap.x) : dev ? dev.x : 50
          const y = ap ? Number(ap.y) : dev ? dev.y : 50
          alarmDots.push({
            id: String(a.id ?? uid('al')),
            x,
            y,
            severity: String(a.severity ?? 'MEDIUM'),
            title: String(a.title ?? 'Alarm'),
            device: String(a.device ?? '—'),
          })
        }
        setAlarms(alarmDots)
      } catch (e) {
        if (seq === floorLoadSeqRef.current) {
          setError(e instanceof Error ? e.message : 'Failed to load floor')
        }
      } finally {
        if (seq === floorLoadSeqRef.current) setLoading(false)
      }
    },
    [orgId, token, clearGeometrySelection],
  )

  useEffect(() => {
    if (siteRequired) {
      setFloors([])
      setFloorId('')
      setLoading(false)
      return
    }
    ;(async () => {
      setLoading(true)
      await loadFloors()
      const dev = await fetchDevices(orgId, token)
      const data = (dev.data as { id?: string; name?: string; model?: string; type?: string; deviceType?: string }[]) ?? []
      setInventoryAps(withDemoInventoryFallback(mapWirelessInventory(data)))
      setLoading(false)
    })()
  }, [orgId, token, loadFloors, locationFilter, siteRequired])

  useReloadOnScopeChange(() => {
    void (async () => {
      setLoading(true)
      setFloorId('')
      setFloors([])
      if (!siteRequired) await loadFloors()
      setLoading(false)
    })()
  }, !siteRequired)

  useEffect(() => {
    if (floorId) void loadFloor(floorId)
  }, [floorId, loadFloor])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey
      if (!mod || e.key.toLowerCase() !== 'z') return
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      e.preventDefault()
      if (e.shiftKey) redoGeometry()
      else undoGeometry()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [redoGeometry, undoGeometry])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return
      const mod = e.metaKey || e.ctrlKey
      if (!mod) return
      const hasSelection = selectedWallId || selectedRoomId || selectedObstacleId || selectedDeviceId
      if (e.code === 'KeyC' && hasSelection) {
        e.preventDefault()
        copySelectedElement()
        return
      }
      if (e.code === 'KeyV' && geometryClipboardRef.current) {
        e.preventDefault()
        pasteElement()
        return
      }
      if (e.code === 'KeyD' && hasSelection) {
        e.preventDefault()
        duplicateSelectedElement()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    copySelectedElement,
    duplicateSelectedElement,
    pasteElement,
    selectedDeviceId,
    selectedObstacleId,
    selectedRoomId,
    selectedWallId,
  ])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      if (isTypingTarget(e.target)) return

      const hasSelection = selectedObstacleId || selectedRoomId || selectedWallId || selectedDeviceId
      if (!hasSelection) return

      e.preventDefault()
      if (selectedObstacleId) deleteSelectedObstacle()
      else if (selectedRoomId) deleteSelectedRoom()
      else if (selectedWallId) deleteSelectedWall()
      else if (selectedDeviceId) deleteSelectedDevice()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    deleteSelectedDevice,
    deleteSelectedObstacle,
    deleteSelectedRoom,
    deleteSelectedWall,
    selectedDeviceId,
    selectedObstacleId,
    selectedRoomId,
    selectedWallId,
  ])

  function pctFromEvent(
    clientX: number,
    clientY: number,
    opts?: { clampOutside?: boolean; geometryBounds?: boolean },
  ) {
    const canvas = canvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    const raw = clientToPct(
      clientX,
      clientY,
      rect,
      widthM,
      heightM,
      PAD,
      opts?.clampOutside,
      viewportRef.current,
    )
    if (!raw) return null
    let { x, y } = raw
    const { lo, hi } = opts?.geometryBounds ? geometryPctBounds() : apPctBounds()
    x = clamp(x, lo, hi)
    y = clamp(y, lo, hi)
    if (snapToGrid && snapGridPct > 0) {
      x = snapPct(x, snapGridPct, lo, hi)
      y = snapPct(y, snapGridPct, lo, hi)
    }
    if (backgroundImage && !opts?.geometryBounds) {
      const bounded = clampApPosition(x, y, 'ceiling', geometry)
      x = bounded.x
      y = bounded.y
    }
    return { x, y }
  }

  function canvasLayout() {
    const canvas = canvasRef.current
    if (!canvas) return null
    return hitTestLayoutFromCanvas(canvas.getBoundingClientRect(), widthM, heightM, PAD, viewportRef.current)
  }

  function markerClientPos(xPct: number, yPct: number, layout: NonNullable<ReturnType<typeof canvasLayout>>) {
    const v = layout.viewport ?? DEFAULT_FLOOR_VIEWPORT
    const cx = layout.ox + (xPct / 100) * layout.fw
    const cy = layout.oy + (yPct / 100) * layout.fh
    return {
      x: layout.left + v.panX + v.scale * cx,
      y: layout.top + v.panY + v.scale * cy,
      scale: v.scale,
    }
  }

  function hitAlarm(clientX: number, clientY: number): FloorAlarmDot | null {
    if (!layers.alarms) return null
    const canvas = canvasRef.current
    if (!canvas) return null
    const layout = canvasLayout()
    if (!layout) return null
    for (let i = alarms.length - 1; i >= 0; i--) {
      const a = alarms[i]
      const { x: ax, y: ay, scale } = markerClientPos(a.x, a.y, layout)
      if (Math.hypot(clientX - ax, clientY - ay) < 18 * scale) return a
    }
    return null
  }

  function hitDevice(clientX: number, clientY: number): FloorDevice | null {
    if (!layers.devices) return null
    const layout = canvasLayout()
    if (!layout) return null
    for (let i = geometry.devices.length - 1; i >= 0; i--) {
      const d = geometry.devices[i]
      const show = d.type === 'camera' ? layers.cameras : d.type === 'sensor' ? layers.sensors : true
      if (!show) continue
      const dx = markerClientPos(d.x, d.y, layout)
      if (Math.hypot(clientX - dx.x, clientY - dx.y) < 20 * dx.scale) return d
    }
    return null
  }

  function onApMountChange(patch: {
    mount?: PlacedAp['mount']
    mountHeightM?: number
    mountOrientationDeg?: number
  }) {
    if (!selectedApId) return
    setAps(prev =>
      prev.map(a => (a.id === selectedApId ? { ...a, ...patch } : a)),
    )
    if (mountSaveTimer.current) clearTimeout(mountSaveTimer.current)
    mountSaveTimer.current = setTimeout(() => {
      setAps(prev => {
        const ap = prev.find(a => a.id === selectedApId)
        if (ap) void persistAp(ap)
        return prev
      })
    }, 450)
  }

  function onTxPowerChange(v: number) {
    if (!selectedApId) return
    setAps(prev => prev.map(a => (a.id === selectedApId ? { ...a, txPowerDbm: v } : a)))
    if (txSaveTimer.current) clearTimeout(txSaveTimer.current)
    txSaveTimer.current = setTimeout(() => {
      setAps(prev => {
        const ap = prev.find(a => a.id === selectedApId)
        if (ap) void persistAp(ap)
        return prev
      })
    }, 450)
  }

  function persistSnapGrid(nextSnap: boolean, nextPct: number) {
    if (!floorId) return
    void updateNetworkingFloor(floorId, orgId, token, { snapGridPct: nextSnap ? nextPct : 0 })
  }

  async function persistAp(ap: PlacedAp) {
    if (!floorId || !ap.id) return
    setSaving(true)
    try {
      await updateNetworkingFloorAp(floorId, ap.id, orgId, token, {
        x: ap.x,
        y: ap.y,
        txPowerDbm: ap.txPowerDbm ?? 15,
        mount: ap.mount ?? 'ceiling',
        mountHeightM: ap.mountHeightM ?? defaultMountHeight(ap.mount),
        ...(ap.mount === 'wall'
          ? { mountOrientationDeg: ap.mountOrientationDeg ?? 0 }
          : {}),
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const nudgeSelectedByKeyboard = useCallback(
    (dx: number, dy: number) => {
      if (tool !== 'select') return false

      if (selectedApId) {
        const ap = aps.find(a => a.id === selectedApId)
        if (!ap) return false
        const reconciled = reconcileApAfterMove(ap, ap.x + dx, ap.y + dy, geometry, widthM, heightM)
        setAps(prev => prev.map(a => (a.id === selectedApId ? reconciled : a)))
        if (apNudgeSaveTimer.current) clearTimeout(apNudgeSaveTimer.current)
        apNudgeSaveTimer.current = setTimeout(() => void persistAp(reconciled), 400)
        return true
      }

      if (selectedObstacleId) {
        updateGeometry(g => ({
          ...g,
          obstacles: g.obstacles.map(o => {
            if (o.id !== selectedObstacleId) return o
            const pos = clampObstacleCenter(o.x + dx, o.y + dy, o, widthM, heightM, g)
            return { ...o, x: pos.x, y: pos.y }
          }),
        }))
        return true
      }

      if (selectedWallId) {
        updateGeometry(g => ({
          ...g,
          walls: g.walls.map(w => {
            if (w.id !== selectedWallId) return w
            const moved = clampWallSegmentTranslate(w.x1 + dx, w.y1 + dy, w.x2 + dx, w.y2 + dy, g)
            return { ...w, x1: moved.x1, y1: moved.y1, x2: moved.x2, y2: moved.y2 }
          }),
        }))
        return true
      }

      if (selectedDeviceId) {
        const { lo, hi } = apPctBounds()
        updateGeometry(g => ({
          ...g,
          devices: g.devices.map(d =>
            d.id === selectedDeviceId
              ? { ...d, x: clamp(d.x + dx, lo, hi), y: clamp(d.y + dy, lo, hi) }
              : d,
          ),
        }))
        return true
      }

      return false
    },
    [
      aps,
      geometry,
      selectedApId,
      selectedDeviceId,
      selectedObstacleId,
      selectedWallId,
      tool,
      updateGeometry,
      widthM,
      heightM,
    ],
  )

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return
      if (tool !== 'select') return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      let dx = 0
      let dy = 0
      switch (e.key) {
        case 'ArrowLeft':
          dx = -1
          break
        case 'ArrowRight':
          dx = 1
          break
        case 'ArrowUp':
          dy = -1
          break
        case 'ArrowDown':
          dy = 1
          break
        default:
          return
      }

      const baseStep = snapToGrid && snapGridPct > 0 ? snapGridPct : 1
      const step = e.shiftKey ? baseStep * 5 : baseStep
      if (nudgeSelectedByKeyboard(dx * step, dy * step)) {
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [nudgeSelectedByKeyboard, snapGridPct, snapToGrid, tool])

  const removePlacedAp = useCallback(
    (apId: string) => {
      if (!floorId) return
      void removeNetworkingFloorAp(floorId, apId, orgId, token)
        .then(() => {
          invalidatePendingFloorLoad()
          setAps(prev => prev.filter(a => a.id !== apId))
          setSelectedApId(prev => (prev === apId ? null : prev))
          setDragApId(prev => (prev === apId ? null : prev))
        })
        .catch(e => setError(e instanceof Error ? e.message : 'Remove AP failed'))
    },
    [floorId, orgId, token, invalidatePendingFloorLoad],
  )

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      if (isTypingTarget(e.target)) return
      if (!selectedApId) return
      e.preventDefault()
      removePlacedAp(selectedApId)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [removePlacedAp, selectedApId])

  const applyRecommendationFix = useCallback(
    async (rec: FloorRecommendation) => {
      if (!rec.fix) return
      setApplyingRecommendationId(rec.id)
      setError(null)
      try {
        if (rec.action === 'add_ap' && !pickInventoryApName(inventoryAps, aps)) {
          setNoApDialog(noAvailableApDialogContent(inventoryAps, aps, geometry, widthM, heightM))
          return
        }

        const fix = rec.fix
        let nextAps = aps.map(a => ({ ...a }))
        let applied = false

        const mem = applyRecommendationFixInMemory(rec, nextAps, geometry, widthM, heightM, {
          inventoryAps,
        })
        if (mem.applied) {
          nextAps = mem.aps
          applied = true
        }

        if (mem.newAp && floorId) {
          const inv = inventoryAps.find(a => a.name === mem.newAp!.apName)
          const gapPos = { x: mem.newAp.x, y: mem.newAp.y }
          const minDistM = Math.min(
            ...nextAps.map(ap =>
              Math.hypot(((ap.x - gapPos.x) / 100) * widthM, ((ap.y - gapPos.y) / 100) * heightM),
            ),
          )
          if (minDistM < MIN_GAP_AP_SEPARATION_M) {
            setError('No clear gap found — move APs apart or pick a spot manually.')
            return
          }
          const p = await placeNetworkingFloorAp(
            floorId,
            orgId,
            token,
            gapApPlaceRequestBody(mem.newAp, inv?.id),
          )
          nextAps = upsertPlacedAp(nextAps, {
            id: String(p.id),
            ap: String(p.ap),
            x: Number(p.x),
            y: Number(p.y),
            clients: Number(p.clients ?? 0),
            rssiZone: String(p.rssiZone ?? 'medium'),
            txPowerDbm: mem.newAp.txPowerDbm,
            channel: mem.newAp.channel,
            deviceId: inv?.id,
            mount: mem.newAp.mount,
            mountHeightM: mem.newAp.mountHeightM,
            mountOrientationDeg: mem.newAp.mountOrientationDeg,
            band: mem.newAp.band,
          })
          applied = true
        } else if (fix?.newApAt && floorId && !mem.newAp) {
          const apName = pickInventoryApName(inventoryAps, nextAps)
          if (!apName) {
            setNoApDialog(noAvailableApDialogContent(inventoryAps, nextAps, geometry, widthM, heightM))
            return
          }
          const grid = computeHeatmapGrid(nextAps, geometry, widthM, heightM)
          const gapPos =
            findGapApPosition(nextAps, grid, widthM, heightM, geometry) ?? fix.newApAt
          const minDistM = Math.min(
            ...nextAps.map(ap =>
              Math.hypot(((ap.x - gapPos.x) / 100) * widthM, ((ap.y - gapPos.y) / 100) * heightM),
            ),
          )
          if (minDistM < MIN_GAP_AP_SEPARATION_M) {
            setError('No clear gap found — move APs apart or pick a spot manually.')
            return
          }
          const placement = buildGapApPlacement(
            apName,
            inventoryAps,
            gapPos,
            nextAps,
            geometry,
            widthM,
            heightM,
          )
          const inv = inventoryAps.find(a => a.name === apName)
          const p = await placeNetworkingFloorAp(
            floorId,
            orgId,
            token,
            gapApPlaceRequestBody(placement, inv?.id),
          )
          nextAps = upsertPlacedAp(nextAps, {
            id: String(p.id),
            ap: String(p.ap),
            x: Number(p.x),
            y: Number(p.y),
            clients: Number(p.clients ?? 0),
            rssiZone: String(p.rssiZone ?? 'medium'),
            txPowerDbm: placement.txPowerDbm,
            channel: placement.channel,
            deviceId: inv?.id,
            mount: placement.mount,
            mountHeightM: placement.mountHeightM,
            mountOrientationDeg: placement.mountOrientationDeg,
            band: placement.band,
          })
          applied = true
        }

        if (fix.perimeterWalls) {
          updateGeometry(g => ({
            ...g,
            walls: [...g.walls, ...createPerimeterWalls(uid('rf-wall'))],
            layers: { ...g.layers, walls: true },
          }))
          setTool('select')
          applied = true
        } else if (fix.enableLayer) {
          updateGeometry(g => ({ ...g, layers: { ...g.layers, [fix.enableLayer!]: true } }))
          applied = true
        }

        if (fix.obstacleType && fix.obstacleAt) {
          const base = createObstacle(fix.obstacleType, fix.obstacleAt.x, fix.obstacleAt.y, widthM, heightM)
          updateGeometry(g => ({
            ...g,
            obstacles: [...g.obstacles, { ...base, id: uid('obs') }],
          }))
          applied = true
        }

        if (fix.switchTool) setTool(fix.switchTool)

        if (
          applied &&
          nextAps.length >= 2 &&
          rec.action !== 'draw_walls' &&
          rec.action !== 'draw_obstacles'
        ) {
          const chPlan = applyChannelPlan(nextAps, geometry.bandGHz, widthM, heightM)
          nextAps = chPlan.aps
          updateGeometry(g => ({
            ...g,
            apChannels: { ...(g.apChannels ?? {}), ...chPlan.channels },
            heatmapLayer:
              rec.action === 'adjust_power' || rec.action === 'relocate_ap' ? 'interference' : g.heatmapLayer,
            layers: { ...g.layers, heatmap: true },
          }))
        } else if (rec.action === 'adjust_power' || fix.moveApId) {
          updateGeometry(g => ({
            ...g,
            heatmapLayer: 'interference',
            layers: { ...g.layers, heatmap: true },
          }))
        }

        if (!applied) {
          setError('Could not apply this fix automatically — try Optimize floor plan or adjust APs manually.')
          return
        }

        invalidatePendingFloorLoad()
        setAps(nextAps)
        const toPersist = nextAps.filter(a => {
          const orig = aps.find(o => o.id === a.id)
          if (!orig) return false
          return (
            orig.x !== a.x ||
            orig.y !== a.y ||
            (orig.txPowerDbm ?? 15) !== (a.txPowerDbm ?? 15)
          )
        })
        const newOnes = nextAps.filter(a => !aps.some(o => o.id === a.id))
        await Promise.all(toPersist.map(ap => persistAp(ap)))
        if (newOnes.length === 0 && toPersist.length > 0) {
          setSelectedApId(toPersist[0].id)
        }
        setOptimizationResult(null)
        setSidebarTab('rf')
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not apply RF fix')
      } finally {
        setApplyingRecommendationId(null)
      }
    },
    [aps, floorId, heightM, inventoryAps, orgId, token, updateGeometry, widthM, geometry.bandGHz, invalidatePendingFloorLoad],
  )

  function upsertPlacedAp(list: PlacedAp[], ap: PlacedAp): PlacedAp[] {
    const idx = list.findIndex(a => a.id === ap.id)
    if (idx >= 0) {
      const next = [...list]
      next[idx] = { ...next[idx], ...ap }
      return next
    }
    return [...list, ap]
  }

  const applyOptimizationResult = useCallback(
    async (result: FloorPlanOptimizationResult, includeNewAps: boolean) => {
      if (!floorId) return
      setApplyingOptimization(true)
      setOptimizationSaving(true)
      const savingStartedAt = Date.now()
      setError(null)
      try {
        const updatedById = new Map(aps.map(ap => [ap.id, { ...ap }]))
        for (const change of result.changes) {
          const cur = updatedById.get(change.apId)
          if (!cur) continue
          if (change.x != null) cur.x = change.x
          if (change.y != null) cur.y = change.y
          if (change.txPowerDbm != null) cur.txPowerDbm = change.txPowerDbm
          if (change.mountOrientationDeg != null) cur.mountOrientationDeg = change.mountOrientationDeg
          if (change.mountHeightM != null) cur.mountHeightM = change.mountHeightM
        }

        const mergedChannels: Record<string, number> = {
          ...(geometry.apChannels ?? {}),
          ...result.channels,
        }
        for (const ap of updatedById.values()) {
          const ch = mergedChannels[ap.id]
          if (ch != null) ap.channel = ch
        }

        const added: PlacedAp[] = []
        const newApsToPlace = includeNewAps ? result.newAps : []
        for (const n of newApsToPlace) {
          const inv = inventoryAps.find(a => a.name === n.apName)
          const defaults = apDefaultsFromInventory(inv, geometry.bandGHz)
          const p = await placeNetworkingFloorAp(floorId, orgId, token, {
            apName: n.apName,
            x: n.x,
            y: n.y,
            txPowerDbm: n.txPowerDbm ?? defaults.txPowerDbm,
            deviceId: inv?.id,
          })
          const ap: PlacedAp = {
            id: String(p.id),
            ap: String(p.ap),
            x: Number(p.x),
            y: Number(p.y),
            clients: Number(p.clients ?? 0),
            rssiZone: String(p.rssiZone ?? 'medium'),
            txPowerDbm: n.txPowerDbm ?? defaults.txPowerDbm,
            channel: n.channel,
            deviceId: inv?.id,
            mount: n.mount ?? defaults.mount,
            mountHeightM: n.mountHeightM ?? defaults.mountHeightM,
            mountOrientationDeg: n.mountOrientationDeg,
            band: defaults.band,
          }
          mergedChannels[ap.id] = n.channel
          added.push(ap)
        }

        const nextAps = [...Array.from(updatedById.values()), ...added].map(ap => ({
          ...ap,
          channel: mergedChannels[ap.id] ?? ap.channel,
        }))

        const nextGeometry = {
          ...geometry,
          apChannels: mergedChannels,
          heatmapLayer: 'rssi' as const,
          layers: { ...geometry.layers, heatmap: true },
        }

        const reconciled = applyOptimizationMetrics(result, nextAps, nextGeometry, widthM, heightM)

        invalidatePendingFloorLoad()
        setAps(nextAps)
        updateGeometry(g => ({
          ...g,
          apChannels: mergedChannels,
          heatmapLayer: 'rssi',
          layers: { ...g.layers, heatmap: true },
        }))
        setOptimizationResult(reconciled)
        setSidebarTab('rf')

        await Promise.all(
          reconciled.changes.map(change => {
            const ap = nextAps.find(a => a.id === change.apId)
            return ap ? persistAp(ap) : Promise.resolve()
          }),
        )
        await paceSavingPhase(savingStartedAt)
        setOptimizationComplete(true)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not apply optimization')
        setOptimizationOverlayOpen(false)
        setOptimizationComplete(false)
      } finally {
        setApplyingOptimization(false)
        setOptimizationSaving(false)
        setPendingOptimization(null)
      }
    },
    [aps, floorId, geometry, heightM, inventoryAps, invalidatePendingFloorLoad, orgId, token, updateGeometry, widthM],
  )

  const runFloorOptimization = useCallback(async () => {
    if (!floorId || aps.length === 0) return
    setOptimizing(true)
    setOptimizationOverlayOpen(true)
    setOptimizationComplete(false)
    setOptimizationProgress(null)
    setOptimizationSaving(false)
    setError(null)
    setOptimizationResult(null)
    setPendingOptimization(null)
    try {
      const result = await optimizeFloorPlan(aps, geometry, widthM, heightM, {
        inventoryAps,
        expectedClients: capacity.clients,
        onProgress: setOptimizationProgress,
      })

      if (result.newAps.length > 0) {
        setPendingOptimization(result)
        setOptimizationComplete(false)
        setOptimizationOverlayOpen(false)
        return
      }

      await applyOptimizationResult(result, true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Floor optimization failed')
      setOptimizationOverlayOpen(false)
      setOptimizationComplete(false)
    } finally {
      setOptimizing(false)
    }
  }, [applyOptimizationResult, aps, capacity.clients, floorId, geometry, heightM, inventoryAps, widthM])

  const dismissOptimizationOverlay = useCallback(() => {
    setOptimizationOverlayOpen(false)
    setOptimizationComplete(false)
    setOptimizationProgress(null)
    setOptimizationSaving(false)
  }, [])

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    const layout = canvasLayout()
    if (layout) {
      if (layers.clients) {
        const clientHit = hitClientMarker(e.clientX, e.clientY, clients, layout)
        if (clientHit) {
          selectClientById(clientHit.id)
          ;(e.target as HTMLCanvasElement).setPointerCapture(e.pointerId)
          return
        }
      }

      const deleteHit = hitApDeleteButton(e.clientX, e.clientY, apsWithCounts, layout)
      if (deleteHit) {
        e.preventDefault()
        removePlacedAp(deleteHit.id)
        return
      }
    }

    const pct = pctFromEvent(e.clientX, e.clientY)
    if (!pct) return

    if (layout) {
      const apHit = hitApMarker(e.clientX, e.clientY, apsWithCounts, layout)
      if (apHit) {
        setDragApId(apHit.id)
        setSelectedApId(apHit.id)
        setSelectedClientId(null)
        setSelectedAlarm(null)
        setSelectedWallId(null)
        setSelectedRoomId(null)
        setSelectedObstacleId(null)
        setSelectedDeviceId(null)
        setSidebarTab('inspect')
        ;(e.target as HTMLCanvasElement).setPointerCapture(e.pointerId)
        return
      }
    }

    const devHit = hitDevice(e.clientX, e.clientY)
    if (devHit) {
      const { id: _id, ...data } = devHit
      storeGeometryClipboard({ kind: 'device', data })
      setSelectedDeviceId(devHit.id)
      setSelectedApId(null)
      setSelectedAlarm(null)
      setSelectedWallId(null)
      setSelectedRoomId(null)
      setSelectedObstacleId(null)
      setSidebarTab('inspect')
      liveDragGeomRef.current = { kind: 'device', id: devHit.id, x: devHit.x, y: devHit.y }
      ;(e.target as HTMLCanvasElement).setPointerCapture(e.pointerId)
      return
    }

    const obsHit = hitObstacle(pct.x, pct.y, geometry.obstacles, widthM, heightM)
    if (obsHit) {
      const { id: _id, ...data } = obsHit
      storeGeometryClipboard({ kind: 'obstacle', data })
      setSelectedObstacleId(obsHit.id)
      setSelectedRoomId(null)
      setSelectedWallId(null)
      setSelectedDeviceId(null)
      setSelectedApId(null)
      setSelectedAlarm(null)
      setSidebarTab('inspect')
      liveDragGeomRef.current = { kind: 'obstacle', id: obsHit.id, x: obsHit.x, y: obsHit.y }
      ;(e.target as HTMLCanvasElement).setPointerCapture(e.pointerId)
      return
    }

    const roomHit = hitRoom(pct.x, pct.y, geometry.rooms)
    if (roomHit) {
      const { id: _id, ...data } = roomHit
      storeGeometryClipboard({ kind: 'room', data })
      setSelectedRoomId(roomHit.id)
      setSelectedObstacleId(null)
      setSelectedWallId(null)
      setSelectedDeviceId(null)
      setSelectedApId(null)
      setSelectedAlarm(null)
      setSidebarTab('inspect')
      liveDragGeomRef.current = {
        kind: 'room',
        id: roomHit.id,
        x: roomHit.x,
        y: roomHit.y,
        w: roomHit.w,
        h: roomHit.h,
        anchorX: pct.x,
        anchorY: pct.y,
        baseX: roomHit.x,
        baseY: roomHit.y,
      }
      ;(e.target as HTMLCanvasElement).setPointerCapture(e.pointerId)
      return
    }

    const wallHit = hitWall(pct.x, pct.y, geometry.walls)
    if (wallHit) {
      const { id: _id, ...data } = wallHit
      storeGeometryClipboard({ kind: 'wall', data })
      setSelectedWallId(wallHit.id)
      setSelectedObstacleId(null)
      setSelectedRoomId(null)
      setSelectedDeviceId(null)
      setSelectedApId(null)
      setSelectedAlarm(null)
      setSidebarTab('inspect')
      liveDragGeomRef.current = {
        kind: 'wall',
        id: wallHit.id,
        x1: wallHit.x1,
        y1: wallHit.y1,
        x2: wallHit.x2,
        y2: wallHit.y2,
        anchorX: pct.x,
        anchorY: pct.y,
        baseX1: wallHit.x1,
        baseY1: wallHit.y1,
        baseX2: wallHit.x2,
        baseY2: wallHit.y2,
      }
      ;(e.target as HTMLCanvasElement).setPointerCapture(e.pointerId)
      return
    }

    const alarmHit = hitAlarm(e.clientX, e.clientY)
    if (alarmHit) {
      setSelectedAlarm(alarmHit)
      setSelectedApId(null)
      setSelectedDeviceId(null)
      setSelectedWallId(null)
      setSelectedRoomId(null)
      setSelectedObstacleId(null)
      setSidebarTab('inspect')
      return
    }

    if (tool === 'select') {
      panDragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        originPanX: viewportRef.current.panX,
        originPanY: viewportRef.current.panY,
        moved: false,
      }
      ;(e.target as HTMLCanvasElement).setPointerCapture(e.pointerId)
      return
    }

    if (tool === 'wall' || tool === 'room') {
      const geomPct = pctFromEvent(e.clientX, e.clientY, { geometryBounds: true })
      if (!geomPct) return
      setDrawStart(geomPct)
      setDrawCurrent(geomPct)
      drawPreviewRef.current = { start: geomPct, current: geomPct }
      ;(e.target as HTMLCanvasElement).setPointerCapture(e.pointerId)
      return
    }

    if (tool === 'obstacle') {
      const base = createObstacle(obstacleType, pct.x, pct.y, widthM, heightM, obstacleDims)
      const pos = clampObstacleCenter(base.x, base.y, base, widthM, heightM, geometry)
      const created = { ...base, id: uid('obs'), x: pos.x, y: pos.y }
      updateGeometry(g => ({
        ...g,
        obstacles: [...g.obstacles, created],
      }))
      const { id: _id, ...data } = created
      storeGeometryClipboard({ kind: 'obstacle', data })
      setSelectedObstacleId(created.id)
      setSidebarTab('inspect')
      return
    }

    if (tool === 'device') {
      const created = {
        id: uid('dev'),
        type: deviceType,
        name: `${deviceType}-${geometry.devices.length + 1}`,
        x: pct.x,
        y: pct.y,
        status: 'healthy' as const,
        heightM: 2.5,
        mount: 'ceiling',
      }
      updateGeometry(g => ({
        ...g,
        devices: [...g.devices, created],
      }))
      setSelectedDeviceId(created.id)
      setSidebarTab('inspect')
    }
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    const pan = panDragRef.current
    if (pan) {
      const dx = e.clientX - pan.startX
      const dy = e.clientY - pan.startY
      if (Math.hypot(dx, dy) > 2) {
        pan.moved = true
        if (!isPanning) setIsPanning(true)
      }
      updateViewport({
        ...viewportRef.current,
        panX: pan.originPanX + dx,
        panY: pan.originPanY + dy,
      })
      return
    }

    const geomDragKind = liveDragGeomRef.current?.kind
    const geometryBounds =
      tool === 'wall' ||
      tool === 'room' ||
      geomDragKind === 'wall' ||
      geomDragKind === 'room'
    const pct = pctFromEvent(e.clientX, e.clientY, {
      clampOutside: !!dragApId || !!liveDragGeomRef.current,
      geometryBounds,
    })
    if (pct && layers.clients) {
      const moveLayout = canvasLayout()
      const clientHover = moveLayout ? hitClientMarker(e.clientX, e.clientY, clients, moveLayout) : null
      if (clientHover) {
        setHover({
          x: e.nativeEvent.offsetX,
          y: e.nativeEvent.offsetY,
          label: `${clientHover.label ?? clientHover.id} · ${clientHover.rssi} dBm · ${clientHover.ap}`,
          ap: clientHover.ap,
        })
        return
      }
    }

    if (pct && layers.heatmap && apsWithCounts.length > 0) {
      const m = rfMetricsAtPoint(pct.x, pct.y, apsWithCounts, geometry, widthM, heightM)
      const near = apsWithCounts.reduce<{ d: number; ap?: PlacedAp }>(
        (best, ap) => {
          const d = Math.hypot(pct.x - ap.x, pct.y - ap.y)
          return d < best.d ? { d, ap } : best
        },
        { d: Infinity },
      ).ap
      setHover({
        x: e.nativeEvent.offsetX,
        y: e.nativeEvent.offsetY,
        label: formatRfMetric(geometry.heatmapLayer, m),
        ap: near?.ap,
      })
    } else {
      setHover(null)
    }

    if (drawStart && (tool === 'wall' || tool === 'room') && pct) {
      setDrawCurrent(pct)
      drawPreviewRef.current = { start: drawStart, current: pct }
      paintRef.current()
      return
    }

    if (dragApId && pct) {
      liveDragApRef.current = { id: dragApId, x: pct.x, y: pct.y }
      setAps(prev => prev.map(a => (a.id === dragApId ? { ...a, x: pct.x, y: pct.y } : a)))
      paintRef.current()
      return
    }

    const geomDrag = liveDragGeomRef.current
    if (geomDrag && pct) {
      if (geomDrag.kind === 'obstacle') {
        const obs = geometry.obstacles.find(o => o.id === geomDrag.id)
        const pos = obs
          ? clampObstacleCenter(pct.x, pct.y, obs, widthM, heightM, geometry)
          : { x: pct.x, y: pct.y }
        liveDragGeomRef.current = { ...geomDrag, x: pos.x, y: pos.y }
      } else if (geomDrag.kind === 'device') {
        liveDragGeomRef.current = { ...geomDrag, x: pct.x, y: pct.y }
      } else if (geomDrag.kind === 'room') {
        const dx = pct.x - geomDrag.anchorX
        const dy = pct.y - geomDrag.anchorY
        liveDragGeomRef.current = {
          ...geomDrag,
          x: clamp(geomDrag.baseX + dx, 0, 100 - geomDrag.w),
          y: clamp(geomDrag.baseY + dy, 0, 100 - geomDrag.h),
        }
      } else if (geomDrag.kind === 'wall') {
        const dx = pct.x - geomDrag.anchorX
        const dy = pct.y - geomDrag.anchorY
        const moved = clampWallSegmentTranslate(
          geomDrag.baseX1 + dx,
          geomDrag.baseY1 + dy,
          geomDrag.baseX2 + dx,
          geomDrag.baseY2 + dy,
          geometry,
        )
        liveDragGeomRef.current = {
          ...geomDrag,
          x1: moved.x1,
          y1: moved.y1,
          x2: moved.x2,
          y2: moved.y2,
        }
      }
      paintRef.current()
      return
    }
    liveDragApRef.current = null
  }

  function onPointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    const geometryBounds = tool === 'wall' || tool === 'room'
    const pct = pctFromEvent(e.clientX, e.clientY, { geometryBounds })
    if (drawStart && pct && tool === 'wall') {
      if (Math.hypot(pct.x - drawStart.x, pct.y - drawStart.y) > 1) {
        const mat = WALL_MATERIALS.find(m => m.id === wallMaterial)
        const seg = createWallSegment(
          drawStart.x,
          drawStart.y,
          pct.x,
          pct.y,
          wallMaterial,
          wallDims,
          mat?.attenuationDb,
        )
        updateGeometry(g => ({
          ...g,
          walls: [...g.walls, { ...seg, id: uid('wall') }],
        }))
      }
    }
    if (drawStart && pct && tool === 'room') {
      const x = Math.min(drawStart.x, pct.x)
      const y = Math.min(drawStart.y, pct.y)
      const w = Math.abs(pct.x - drawStart.x)
      const h = Math.abs(pct.y - drawStart.y)
      if (w > 2 && h > 2) {
        updateGeometry(g => ({
          ...g,
          rooms: [...g.rooms, { id: uid('room'), name: `Room ${g.rooms.length + 1}`, x, y, w, h }],
        }))
      }
    }
    setDrawStart(null)
    setDrawCurrent(null)
    drawPreviewRef.current = { start: null, current: null }

    if (dragApId) {
      setAps(prev => {
        const ap = prev.find(a => a.id === dragApId)
        if (!ap) return prev
        const reconciled = pct ? reconcileApAfterMove(ap, pct.x, pct.y, geometry, widthM, heightM) : ap
        void persistAp(reconciled)
        return prev.map(a => (a.id === dragApId ? reconciled : a))
      })
      setDragApId(null)
      liveDragApRef.current = null
    }

    if (liveDragGeomRef.current) {
      const d = liveDragGeomRef.current
      if (d.kind === 'obstacle') {
        updateGeometry(g => ({
          ...g,
          obstacles: g.obstacles.map(o => (o.id === d.id ? { ...o, x: d.x, y: d.y } : o)),
        }))
      } else if (d.kind === 'device') {
        updateGeometry(g => ({
          ...g,
          devices: g.devices.map(dev => (dev.id === d.id ? { ...dev, x: d.x, y: d.y } : dev)),
        }))
      } else if (d.kind === 'room') {
        updateGeometry(g => ({
          ...g,
          rooms: g.rooms.map(r => (r.id === d.id ? { ...r, x: d.x, y: d.y } : r)),
        }))
      } else if (d.kind === 'wall') {
        updateGeometry(g => ({
          ...g,
          walls: g.walls.map(w =>
            w.id === d.id ? { ...w, x1: d.x1, y1: d.y1, x2: d.x2, y2: d.y2 } : w,
          ),
        }))
      }
      liveDragGeomRef.current = null
    }

    const pan = panDragRef.current
    if (pan) {
      if (!pan.moved && tool === 'select') clearGeometrySelection()
      panDragRef.current = null
      setIsPanning(false)
    }

    try {
      ;(e.target as HTMLCanvasElement).releasePointerCapture(e.pointerId)
    } catch {
      /* ignore */
    }
  }

  function zoomFloorAt(factor: number, clientX?: number, clientY?: number) {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const p =
      clientX != null && clientY != null
        ? clientToCanvasPoint(clientX, clientY, rect)
        : { x: rect.width / 2, y: rect.height / 2 }
    updateViewport(zoomViewportAtCanvasPoint(viewportRef.current, p.x, p.y, factor))
  }

  function onCanvasWheel(e: React.WheelEvent<HTMLCanvasElement>) {
    e.preventDefault()
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12
    zoomFloorAt(factor, e.clientX, e.clientY)
  }

  if (siteRequired) {
    return (
      <div className="floor-plan-editor rounded-lg border border-dashed border-mg-border p-8 text-center">
        <MapPin size={32} className="mx-auto mb-3 text-mg-muted" />
        <p className="text-sm font-medium text-mg-text">Select a site to manage floor plans</p>
        <p className="mt-1 text-xs text-mg-muted">
          Organisation → Location → Floor. Pick a site in the header scope picker ({scopeLabel} is org-wide).
        </p>
      </div>
    )
  }

  return (
    <div className="floor-plan-editor floor-plan-pro">
      {floors.length > 0 && (
        <FloorPlanFloorGallery
          floors={floors}
          selectedFloorId={floorId}
          thumbnails={floorThumbnails}
          loadingThumbnails={loadingFloorThumbnails}
          onSelect={setFloorId}
          onAddFloor={() => setShowAddFloor(true)}
        />
      )}
      <div className="floor-plan-toolbar inc-fp-toolbar shrink-0 flex flex-wrap items-center gap-x-2 gap-y-1">
        {floorId && (
          <div className="inc-fp-stats">
            <span className="inc-fp-stat"><strong>{aps.length}</strong> APs</span>
            <span className="inc-fp-stat"><strong>{geometry.walls.length}</strong> walls</span>
            <span className="inc-fp-stat"><strong>{capacity.clients}</strong> clients</span>
            <span className="inc-fp-stat"><strong>{capacity.pct}%</strong> cap</span>
          </div>
        )}
        <div className="inc-fp-actions">
          {floorId && (
            <>
              <Button
                variant={snapToGrid ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 px-2"
                type="button"
                title="Snap to grid"
                onClick={() => {
                  const next = !snapToGrid
                  setSnapToGrid(next)
                  persistSnapGrid(next, snapGridPct)
                }}
              >
                <Grid3x3 size={14} /> Grid
              </Button>
              {snapToGrid && (
                <Select
                  value={String(snapGridPct)}
                  className="h-8 w-[4.25rem] px-1.5 text-xs"
                  title="Grid step (% of floor)"
                  onChange={e => {
                    const pct = Number(e.target.value) || DEFAULT_SNAP_GRID_PCT
                    setSnapGridPct(pct)
                    persistSnapGrid(true, pct)
                  }}
                >
                  {SNAP_GRID_OPTIONS.map(n => (
                    <option key={n} value={n}>
                      {n % 1 === 0 ? n : n.toFixed(1)}%
                    </option>
                  ))}
                </Select>
              )}
              <Button
                variant={layers.heatmap ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 px-2"
                type="button"
                disabled={aps.length === 0}
                title={aps.length === 0 ? 'Add APs to show coverage heatmap' : 'Toggle coverage heatmap'}
                onClick={() =>
                  updateGeometry(g => ({
                    ...g,
                    layers: { ...g.layers, heatmap: !g.layers.heatmap },
                  }))
                }
              >
                <Radio size={14} /> Coverage
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                disabled={floorActionLoading}
                title="Delete floor"
                onClick={() => void handleDeleteFloor()}
              >
                <Trash2 size={14} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => loadFloor(floorId)}
                disabled={loading}
                title="Reload floor"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </Button>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            className="hidden"
            onChange={e => {
              const f = e.target.files?.[0]
              if (f && floorId) {
                void openCropFromFile(f, { kind: 'upload', floorId }, { widthM, heightM, floorName }).catch(
                  () => {},
                )
              }
              e.target.value = ''
            }}
          />
          <Button variant="ghost" size="sm" className="h-8" disabled={!floorId || uploadingBg} onClick={() => fileInputRef.current?.click()}>
            <Upload size={14} /> {uploadingBg ? '…' : 'Plan'}
          </Button>
          {backgroundImage && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                disabled={!floorId || floorActionLoading}
                title="Adjust crop"
                onClick={openRecropDialog}
              >
                <Crop size={14} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() =>
                  floorId &&
                  updateNetworkingFloor(floorId, orgId, token, { backgroundImage: '' }).then(() => setBackgroundImage(null))
                }
              >
                <ImageOff size={14} />
              </Button>
            </>
          )}
          {saving && <Badge variant="info">Saving</Badge>}
          {floorId && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                type="button"
                title="Download map screenshot (PNG)"
                onClick={takeFloorScreenshot}
              >
                <Camera size={14} /> PNG
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                type="button"
                title="Export report with metrics (PNG)"
                onClick={exportFloorReport}
              >
                <FileDown size={14} /> Report
              </Button>
            </>
          )}
        </div>
      </div>

      {showAddFloor && (
        <div className="floor-add-panel rounded-lg border border-mg-border bg-mg-surface p-3">
          <p className="mb-2 text-xs font-semibold text-mg-text">New floor</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-mg-muted">Floor name</label>
              <Input
                value={newFloorName}
                onChange={e => {
                  setNewFloorName(e.target.value)
                  const dims = parseDimensionsFromText(e.target.value)
                  if (dims) {
                    setNewFloorW(String(dims.widthM))
                    setNewFloorH(String(dims.heightM))
                  }
                }}
                placeholder="Level 5 — 120×70 m"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-mg-muted">Width (m)</label>
              <Input value={newFloorW} onChange={e => setNewFloorW(e.target.value)} type="number" className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold text-mg-muted">Height (m)</label>
              <Input value={newFloorH} onChange={e => setNewFloorH(e.target.value)} type="number" className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-mg-muted">Building (optional)</label>
              <Input
                value={newFloorBuilding}
                onChange={e => setNewFloorBuilding(e.target.value)}
                placeholder="HQ Tower A"
                className="mt-1"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-mg-muted">Level (optional)</label>
              <Input
                value={newFloorLevel}
                onChange={e => setNewFloorLevel(e.target.value)}
                placeholder="5"
                className="mt-1"
              />
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <label className="text-xs font-semibold text-mg-muted">Floor plan image (optional)</label>
              <p className="mt-0.5 text-[10px] text-mg-muted">PNG, JPG, or WebP. PDF is not supported — export your blueprint as an image first.</p>
              <input
                ref={createFloorImageRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0]
                  if (f) {
                    if (f.type === 'application/pdf') {
                      setError('PDF floor plans are not supported yet — export as PNG or JPG and upload that file.')
                      e.target.value = ''
                      return
                    }
                    setNewFloorImage(f)
                    setNewFloorImagePreview(URL.createObjectURL(f))
                    const fromFile = parseDimensionsFromText(f.name)
                    if (fromFile) {
                      setNewFloorW(String(fromFile.widthM))
                      setNewFloorH(String(fromFile.heightM))
                    }
                  }
                  e.target.value = ''
                }}
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button variant="secondary" size="sm" onClick={() => createFloorImageRef.current?.click()}>
                  <Upload size={14} /> {newFloorImage ? 'Change image' : 'Upload image'}
                </Button>
                {newFloorImage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setNewFloorImage(null)
                      setNewFloorImagePreview(null)
                    }}
                  >
                    <ImageOff size={14} /> Remove
                  </Button>
                )}
                {newFloorImagePreview && (
                  <span className="text-xs text-mg-muted">{newFloorImage?.name}</span>
                )}
              </div>
              <p className="mt-1 text-[11px] text-mg-muted">
                Leave empty for a black canvas — add walls and obstacles after creation. With an image, you can crop before the floor is created.
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-mg-border pt-4">
            <Button variant="ghost" size="sm" onClick={() => setShowAddFloor(false)}>
              Cancel
            </Button>
            <Button disabled={floorActionLoading} onClick={() => void handleCreateFloor()}>
              {floorActionLoading ? 'Creating…' : 'Create floor'}
            </Button>
          </div>
        </div>
      )}

      {error && <p className="login-alert rounded-md px-2 py-1.5 text-xs">{error}</p>}

      <div className="floor-plan-workspace flex flex-1 flex-col gap-2 lg:flex-row lg:items-stretch">
        <div className="floor-plan-map-col flex min-w-0 flex-1 flex-col gap-2">
          {floorId ? (
            <>
          <div
            ref={wrapRef}
            className={`floor-plan-canvas-wrap relative flex-1 overflow-hidden rounded-lg border border-mg-border bg-slate-900 ${canvasDragOver ? 'floor-plan-canvas-wrap--drag-over' : ''}`}
            onDragOver={e => {
              if (e.dataTransfer.types.includes(FLOOR_PALETTE_MIME)) {
                e.preventDefault()
                setCanvasDragOver(true)
              }
            }}
            onDragLeave={() => setCanvasDragOver(false)}
            onDrop={e => {
              e.preventDefault()
              setCanvasDragOver(false)
              const payload = parsePaletteDrop(e.dataTransfer.getData(FLOOR_PALETTE_MIME))
              const pct = pctFromEvent(e.clientX, e.clientY)
              if (payload && pct) placePaletteItem(payload, pct)
            }}
          >
            {isFloorEmpty && !loading && (
              <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/40 px-6 text-center">
                <p className="text-sm font-semibold text-white">Empty floor</p>
                <p className="mt-1 max-w-sm text-xs text-slate-300">
                  Drag APs from the APs tab onto the map · Draw walls and rooms from the Draw tab
                </p>
              </div>
            )}
            {loading && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-mg-surface/80 text-sm text-mg-muted">
                Loading floor map…
              </div>
            )}
            {optimizationOverlayOpen && (
              <div
                className={`absolute inset-0 z-30 flex items-center justify-center p-4 backdrop-blur-sm ${
                  optimizationComplete ? 'bg-black/65' : 'bg-slate-950/70'
                }`}
              >
                <FloorPlanOptimizationProgressPanel
                  variant="canvas"
                  progress={optimizationProgress}
                  saving={optimizationSaving}
                  complete={optimizationComplete}
                  result={optimizationResult}
                  onDismiss={dismissOptimizationOverlay}
                />
              </div>
            )}
            <canvas
              ref={canvasRef}
              className="floor-plan-canvas absolute inset-0 h-full w-full touch-none"
              style={{
                cursor: isPanning
                  ? 'grabbing'
                  : liveDragGeomRef.current || dragApId
                    ? 'grabbing'
                    : tool === 'select'
                      ? 'grab'
                      : 'crosshair',
              }}
              tabIndex={0}
              aria-label="Floor plan canvas"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={() => setHover(null)}
              onWheel={onCanvasWheel}
            />
            {floorId && (
              <div className="absolute bottom-3 right-3 z-30 flex items-center gap-1 rounded-lg border border-white/10 bg-black/70 p-1 shadow-lg backdrop-blur-sm">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white/90 transition-colors hover:bg-white/10"
                  title="Zoom out"
                  aria-label="Zoom out"
                  onClick={() => zoomFloorAt(1 / 1.15)}
                >
                  <ZoomOut size={16} />
                </button>
                <button
                  type="button"
                  className="min-w-[3.25rem] rounded-md px-1 py-1 text-center text-[11px] font-semibold tabular-nums text-white/90 transition-colors hover:bg-white/10"
                  title="Reset zoom and pan"
                  aria-label="Reset zoom"
                  onClick={() => resetCanvasView()}
                >
                  {viewportZoomPercent(viewport, zoomBaseline)}%
                </button>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white/90 transition-colors hover:bg-white/10"
                  title="Zoom in"
                  aria-label="Zoom in"
                  onClick={() => zoomFloorAt(1.15)}
                >
                  <ZoomIn size={16} />
                </button>
                {!floorViewportAtBaseline(viewport, zoomBaseline) && (
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white/90 transition-colors hover:bg-white/10"
                    title="Fit to view"
                    aria-label="Fit to view"
                    onClick={() => fitCanvasView()}
                  >
                    <Maximize2 size={15} />
                  </button>
                )}
              </div>
            )}
            {hover && layers.heatmap && aps.length > 0 && (
              <div
                className="floor-plan-tooltip pointer-events-none absolute z-20 rounded-lg border border-mg-border bg-mg-surface px-3 py-2 text-xs shadow-lg"
                style={{ left: hover.x + 12, top: hover.y - 48 }}
              >
                <p className="font-semibold text-mg-text">{hover.label}</p>
                {hover.ap && <p className="text-mg-muted">Near {hover.ap}</p>}
              </div>
            )}

            <FloorPlanRfLiveMetrics
              metrics={rfMetrics}
              bandGHz={geometry.bandGHz}
              placedApCount={aps.length}
              recommendationCount={recommendations.length}
              highPriorityCount={recommendations.filter(r => r.priority === 'HIGH').length}
              optimizing={optimizing || optimizationOverlayOpen}
              onOpenRf={() => setSidebarTab('rf')}
              onOptimize={() => void runFloorOptimization()}
            />

            {layers.heatmap && aps.length > 0 && (
              <div className="pointer-events-none absolute right-2 top-2 z-10 rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[10px] text-white/80 backdrop-blur-sm">
                <span>
                  {geometry.heatmapLayer === 'rssi' && 'RSSI'}
                  {geometry.heatmapLayer === 'snr' && 'SNR'}
                  {geometry.heatmapLayer === 'quality' && 'Quality'}
                  {geometry.heatmapLayer === 'utilization' && 'Util'}
                  {geometry.heatmapLayer === 'interference' && 'Interf'}
                  {geometry.heatmapLayer === 'clientDensity' && 'Clients'}
                  {' · '}
                  {geometry.bandGHz} GHz
                </span>
                <div className="floor-rssi-legend-bar mt-1 h-1.5 w-24" />
              </div>
            )}
          </div>
            </>
          ) : (
            <div className="floor-plan-canvas-wrap flex min-h-[240px] flex-1 items-center justify-center rounded-lg border border-dashed border-mg-border bg-mg-surface px-6 py-8 text-center">
              <div>
                <MapPin size={32} className="mx-auto mb-2 text-mg-muted" />
                <p className="text-sm font-semibold text-mg-text">No floor plan yet</p>
                <p className="mt-1 text-xs text-mg-muted">Create a floor to start placing APs and drawing walls.</p>
                <Button className="mt-3" size="sm" onClick={() => setShowAddFloor(true)}>
                  <Plus size={14} /> Add floor
                </Button>
              </div>
            </div>
          )}
        </div>

        {floorId && (
        <FloorPlanSidebar
          tab={sidebarTab}
          onTabChange={setSidebarTab}
          layers={layers}
          geometry={geometry}
          tool={tool}
          wallMaterial={wallMaterial}
          wallDims={wallDims}
          obstacleType={obstacleType}
          obstacleDims={obstacleDims}
          floorWidthM={widthM}
          floorHeightM={heightM}
          floorId={floorId}
          savingFloorSize={savingFloorSize}
          deviceType={deviceType}
          canUndo={history.past.length > 0}
          canRedo={history.future.length > 0}
          selectedAp={selectedAp}
          connectedClients={connectedClientsForAp}
          selectedClient={selectedClient}
          onSelectClient={selectClientById}
          onClientSpeedTestComplete={handleClientSpeedTestComplete}
          onRunClientSpeedTest={handleRunClientSpeedTest}
          selectedDevice={selectedDevice}
          selectedWall={selectedWall}
          selectedRoom={selectedRoom}
          selectedObstacle={selectedObstacle}
          selectedAlarm={selectedAlarm}
          showTopology={layers.topology}
          isFloorEmpty={isFloorEmpty}
          addApName={addApName}
          inventoryAps={inventoryAps}
          recommendations={recommendations}
          rfMetrics={rfMetrics}
          requirementPass={requirementPass}
          interferencePlan={interferencePlan}
          applyingRecommendationId={applyingRecommendationId}
          optimizing={optimizing || optimizationOverlayOpen}
          optimizationOverlayOpen={optimizationOverlayOpen}
          optimizationResult={optimizationResult}
          placedApCount={aps.length}
          placedAps={aps.map(a => ({ id: a.id, ap: a.ap }))}
          selectedApId={selectedApId}
          onSelectAp={id => setSelectedApId(id)}
          onOptimizeFloor={() => void runFloorOptimization()}
          onDismissOptimization={() => setOptimizationResult(null)}
          onApplyRecommendation={rec => void applyRecommendationFix(rec)}
          onSaveFloorSize={(w, h) => saveFloorDimensions(w, h)}
          onUndo={undoGeometry}
          onRedo={redoGeometry}
          onToolChange={setTool}
          onLayerToggle={(key, on) => updateGeometry(g => ({ ...g, layers: { ...g.layers, [key]: on } }))}
          onGeometryPatch={patch => updateGeometry(patch)}
          onWallMaterial={setWallMaterial}
          onWallDims={setWallDims}
          onUpdateWallDims={updateSelectedWallDims}
          onObstacleType={setObstacleType}
          onObstacleDims={setObstacleDims}
          onUpdateObstacleDims={updateSelectedObstacleDims}
          onDeviceType={setDeviceType}
          onTxPower={onTxPowerChange}
          onApMount={onApMountChange}
          onRemoveAp={() => selectedAp && removePlacedAp(selectedAp.id)}
          onDeleteDevice={deleteSelectedDevice}
          onDeleteWall={deleteSelectedWall}
          onDeleteRoom={deleteSelectedRoom}
          onDeleteObstacle={deleteSelectedObstacle}
          onCopyElement={copySelectedElement}
          onDuplicateElement={duplicateSelectedElement}
          onPasteElement={pasteElement}
          geometryClipHint={geometryClipHint}
          hasGeometryClipboard={hasGeometryClipboard}
          onReplaceWallMaterial={replaceSelectedWallMaterial}
          onReplaceObstacleType={replaceSelectedObstacleType}
          onRenameRoom={renameSelectedRoom}
          onUpdateRoomCoverage={updateSelectedRoomCoverage}
          onClearMapElements={clearAllMapElements}
          onDismissAlarm={() => setSelectedAlarm(null)}
          onAddApName={setAddApName}
          onAddAp={() =>
            floorId &&
            placeNetworkingFloorAp(floorId, orgId, token, { apName: addApName, x: 50, y: 50, txPowerDbm: 15 }).then(p =>
              setAps(prev => [
                ...prev,
                {
                  id: String(p.id),
                  ap: String(p.ap),
                  x: Number(p.x),
                  y: Number(p.y),
                  clients: Number(p.clients ?? 0),
                  rssiZone: String(p.rssiZone ?? 'medium'),
                  txPowerDbm: 15,
                },
              ]),
            )
          }
        />
        )}
      </div>

      {pendingOptimization && (
        <FloorPlanOptimizeConfirm
          result={pendingOptimization}
          applying={applyingOptimization}
          onConfirm={includeNewAps => void applyOptimizationResult(pendingOptimization, includeNewAps)}
          onCancel={() => setPendingOptimization(null)}
        />
      )}

      {noApDialog && (
        <FloorPlanNoApDialog content={noApDialog} onClose={() => setNoApDialog(null)} />
      )}

      {cropSession && (
        <FloorPlanCropDialog
          imageUrl={cropSession.imageUrl}
          initialCrop={cropSession.initialCrop}
          suggestedCrop={cropSession.suggestedCrop}
          title={
            cropSession.pending.kind === 'create'
              ? 'Crop plan for new floor'
              : cropSession.pending.kind === 'recrop'
                ? 'Adjust floor plan crop'
                : 'Crop floor plan image'
          }
          onApply={crop => void applyCropSession(crop)}
          onCancel={() => setCropSession(null)}
        />
      )}
    </div>
  )
}
